<template>
  <div class="fts-settings-page fts-autocount">
    <header class="fts-settings-head">
      <div>
        <h1 class="fts-autocount-h1">
          AutoCount
          <span class="fts-autocount-badge" :class="'is-' + headerStatus.tone" v-if="isReady">
            <i aria-hidden="true"></i>{{ headerStatus.text }}
          </span>
        </h1>
        <p>Your AutoCount books in Inistate: lists, reports and GNey answers, read live.</p>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading&hellip;</span>
    </div>

    <!-- An environment whose API predates this page says so, as the other settings pages do. -->
    <div class="fts-settings-banner is-warn" v-else-if="unavailable">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <span>{{ unavailable }}</span>
    </div>

    <template v-else-if="!view">
      <div class="fts-settings-banner is-warn" role="alert">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ error || "AutoCount is not available right now." }}</span>
      </div>
      <div>
        <button type="button" class="fts-settings-btn" @click="load">Try again</button>
      </div>
    </template>

    <template v-else>
      <!-- Inistate's own switch for every connector: nothing here can change it, so no button. -->
      <div class="fts-settings-banner is-warn fts-autocount-platform-off" role="status" v-if="connectorsOff">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>
          <strong>Inistate has turned AutoCount connectors off for now</strong>
          {{ connectorsOff }}
        </span>
      </div>

      <!-- This workspace's own switch for its connector. -->
      <div class="fts-settings-banner is-warn fts-autocount-turned-off" role="status" v-if="turnedOff">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span class="fts-autocount-banner-text">
          <strong>AutoCount is turned off</strong>
          {{ turnedOffLine }}
        </span>
        <button
          type="button"
          class="fts-settings-btn is-primary fts-autocount-turn-on"
          :disabled="switching"
          @click="turnOn"
        >
          {{ switching ? "Turning on…" : "Turn on" }}
        </button>
      </div>
      <div class="fts-settings-banner fts-autocount-turning-on" role="status" v-else-if="turningOn">
        <fts-spinner :size="16" />
        <span>Turning on&hellip; the connector checks in within a minute.</span>
      </div>
      <div class="fts-settings-banner is-warn fts-autocount-turn-on-late" role="status" v-else-if="turnOnTimedOut">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span class="fts-autocount-banner-text">
          The connector has not checked in yet. Check that the Inistate Connector is running on the PC.
        </span>
        <button type="button" class="fts-settings-btn" @click="checkAgain">Check again</button>
      </div>

      <!-- Back from AutoCount Full's Stripe Checkout: the plan turns Full once Stripe tells Inistate. -->
      <div class="fts-settings-banner fts-autocount-confirming" role="status" v-if="confirmingPurchase">
        <fts-spinner :size="16" />
        <span>Confirming your AutoCount Full payment with Stripe&hellip;</span>
      </div>
      <div class="fts-settings-banner fts-autocount-confirm-late" role="status" v-else-if="confirmLate">
        <font-awesome-icon :icon="['fas', 'info-circle']" />
        <span>
          Stripe has not confirmed the payment yet. AutoCount Full turns on as soon as it does; reload this
          page in a few minutes.
        </span>
      </div>

      <div class="fts-settings-banner is-warn fts-autocount-error" role="alert" v-if="error">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <span>{{ error }}</span>
      </div>

      <!-- ===== ready: the AutoCount home ===== -->
      <template v-if="isReady">
        <div class="fts-settings-grid-3">
          <div class="fts-autocount-fact">
            <span class="fts-autocount-hint">This workspace&rsquo;s account book</span>
            <strong>{{ bookName }}</strong>
            <span class="fts-autocount-hint" v-if="book.database">
              <span class="fts-autocount-mono">{{ book.database }}</span><template v-if="book.version"> &middot; AutoCount {{ book.version }}</template>
            </span>
          </div>
          <div class="fts-autocount-fact">
            <span class="fts-autocount-hint">Connector</span>
            <strong>
              {{ machineName }}
              <span class="fts-autocount-badge" :class="'is-' + connectorStatus.tone">
                <i aria-hidden="true"></i>{{ connectorStatus.text }}
              </span>
            </strong>
            <span class="fts-autocount-hint" v-if="connectorLine">{{ connectorLine }}</span>
            <!-- SS06324: the only download link was in step 1, which a set-up workspace never shows again. -->
            <a class="fts-autocount-hint fts-autocount-update" :href="downloadUrl" download>
              <font-awesome-icon :icon="['fas', 'download']" />
              Download the latest connector
            </a>
          </div>
          <div class="fts-autocount-fact">
            <span class="fts-autocount-hint">Access</span>
            <strong>{{ sqlUser ? "SQL login " + sqlUser : "Read only" }}</strong>
            <span class="fts-autocount-hint" v-if="checkedLine">{{ checkedLine }}</span>
          </div>
        </div>

        <!-- AutoCount Full (docs/billing-autocount-connector-pricing-proposal.md §9, SVN): which
             plan the connector is on and the one thing to do about it. Absent while Inistate has
             not switched AutoCount pricing on. -->
        <div class="fts-autocount-plan" :class="'is-' + planStrip.tone" role="status" v-if="planStrip">
          <font-awesome-icon :icon="['fas', planStrip.icon]" class="fts-autocount-plan-icon" />
          <span class="fts-autocount-plan-text">
            <strong>{{ planStrip.title }}</strong>
            <span v-if="planStrip.detail">{{ planStrip.detail }}</span>
          </span>
          <span class="fts-autocount-plan-actions" v-if="planStrip.action || planStrip.billing">
            <button
              v-if="planStrip.action === 'trial'"
              type="button"
              class="fts-settings-btn is-primary fts-autocount-trial"
              :disabled="Boolean(planBusy)"
              @click="startTrial"
            >
              {{ planBusy === "trial" ? "Starting…" : "Start 14-day free trial" }}
            </button>
            <button
              v-else-if="planStrip.action === 'buy'"
              type="button"
              class="fts-settings-btn is-primary fts-autocount-buy"
              :disabled="Boolean(planBusy)"
              @click="buyFull"
            >
              {{ planBusy === "buy" ? "Opening…" : planStrip.buyLabel }}
            </button>
            <router-link
              v-if="planStrip.billing"
              :to="billingRoute"
              class="fts-settings-btn fts-autocount-billing"
            >{{ planStrip.billing }}</router-link>
          </span>
        </div>

        <section class="fts-settings-card" aria-labelledby="fts-autocount-modules">
          <div>
            <h2 id="fts-autocount-modules">Your AutoCount modules</h2>
            <p class="fts-settings-card-sub" v-if="hasModuleList">
              Add the ones this workspace needs and hide the rest. Each reads the book live; nothing is copied into
              Inistate, and nothing in AutoCount changes unless writes to AutoCount are on.
            </p>
            <p class="fts-settings-card-sub" v-else>Each one reads the book live; nothing is copied into Inistate.</p>
          </div>

          <!-- Every module of the AutoCount template: added or not, shown or hidden, on the latest design or not. -->
          <template v-if="hasModuleList">
            <p class="fts-autocount-hint fts-autocount-manage-sum">{{ moduleSummary }}</p>
            <ul class="fts-autocount-manage" aria-label="AutoCount modules">
              <li
                v-for="row in moduleRows"
                :key="row.key"
                class="fts-autocount-manage-row"
                :class="{ 'is-off': row.installed && !row.enabled, 'is-absent': !row.installed }"
              >
                <label v-if="row.addable" class="fts-autocount-manage-pick" :for="'fts-ac-pick-' + row.key">
                  <input
                    type="checkbox"
                    :id="'fts-ac-pick-' + row.key"
                    :value="row.key"
                    v-model="picked"
                    :disabled="settingUp || busy"
                  />
                  <span class="fts-autocount-sr">Choose {{ row.name }}</span>
                </label>
                <span
                  v-else
                  class="fts-autocount-module-icon"
                  :class="{ 'is-locked': row.locked && !row.unavailable }"
                  aria-hidden="true"
                >
                  <font-awesome-icon :icon="['fas', row.icon]" />
                </span>
                <span class="fts-autocount-module-text">
                  <router-link v-if="row.route" :to="row.route" class="fts-autocount-manage-name">
                    <strong>{{ row.name }}</strong>
                  </router-link>
                  <strong v-else>{{ row.name }}</strong>
                  <span>{{ row.note }}</span>
                </span>
                <span class="fts-autocount-badge is-warn" v-if="row.updateAvailable">
                  <i aria-hidden="true"></i>Newer design
                </span>
                <span class="fts-autocount-badge" v-else-if="row.installed && !row.enabled">
                  <i aria-hidden="true"></i>Hidden
                </span>
                <span class="fts-autocount-manage-actions" v-if="row.installed">
                  <button
                    v-if="row.updateAvailable"
                    type="button"
                    class="fts-settings-btn is-primary fts-autocount-update"
                    :disabled="settingUp || busy || cannotReach"
                    @click="updateModules([row.key])"
                  >
                    {{ updatingKeys.indexOf(row.key) >= 0 ? "Updating…" : "Update" }}
                  </button>
                  <button
                    type="button"
                    class="fts-settings-btn fts-autocount-toggle"
                    :disabled="settingUp || busy"
                    @click="toggleModule(row)"
                  >
                    <template v-if="moduleBusy === row.key">{{ row.enabled ? "Hiding…" : "Showing…" }}</template>
                    <template v-else>{{ row.enabled ? "Hide" : "Show" }}</template>
                  </button>
                  <button
                    v-if="!row.updateAvailable"
                    type="button"
                    class="fts-settings-btn fts-autocount-reinstall"
                    :disabled="settingUp || busy || cannotReach"
                    @click="updateModules([row.key])"
                  >
                    {{ updatingKeys.indexOf(row.key) >= 0 ? "Reinstalling…" : "Reinstall" }}
                  </button>
                </span>
              </li>
            </ul>
          </template>

          <div class="fts-autocount-modules" v-else-if="readyModules.length">
            <template v-for="item in readyModules">
              <router-link v-if="item.route" :key="item.key" :to="item.route" class="fts-autocount-module is-link">
                <span class="fts-autocount-module-icon" aria-hidden="true">
                  <font-awesome-icon :icon="['fas', 'table']" />
                </span>
                <span class="fts-autocount-module-text">
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.note }}</span>
                </span>
              </router-link>
              <div v-else :key="item.key" class="fts-autocount-module">
                <span class="fts-autocount-module-icon" aria-hidden="true">
                  <font-awesome-icon :icon="['fas', 'table']" />
                </span>
                <span class="fts-autocount-module-text">
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.note }}</span>
                </span>
              </div>
            </template>
          </div>

          <ul class="fts-autocount-missing" v-if="!hasModuleList && missingModules.length">
            <li v-for="item in missingModules" :key="item.key">
              <font-awesome-icon :icon="['fas', item.failed ? 'times-circle' : 'ban']" class="fts-autocount-missing-icon" />
              <span class="fts-autocount-module-text">
                <strong>{{ item.title }}</strong>
                <span v-if="item.reason">{{ item.reason }}</span>
              </span>
            </li>
          </ul>

          <div class="fts-autocount-manage-foot">
            <button
              v-if="hasModuleList && addableRows.length"
              type="button"
              class="fts-settings-btn is-primary fts-autocount-add"
              :disabled="!pickedKeys.length || settingUp || busy || cannotReach"
              @click="addPicked"
            >
              <fts-spinner v-if="runningAction === 'add'" :size="14" />
              {{ addLabel }}
            </button>
            <button
              v-if="hasModuleList && updatableKeys.length > 1"
              type="button"
              class="fts-settings-btn fts-autocount-update-all"
              :disabled="settingUp || busy || cannotReach"
              @click="updateModules(updatableKeys)"
            >
              <fts-spinner v-if="runningAction === 'update' && updatingKeys.length > 1" :size="14" />
              {{ runningAction === "update" && updatingKeys.length > 1 ? "Updating…" : "Update all " + updatableKeys.length }}
            </button>
            <!-- SS06269: the template's shortcuts and charts for the modules shown here, back on the
                 dashboard. It writes to Inistate only, so a connector that is off does not hold it. -->
            <button
              v-if="canSetUpDashboard"
              type="button"
              class="fts-settings-btn fts-autocount-dashboard"
              title="Puts the AutoCount shortcuts and charts on this workspace's dashboard again"
              :disabled="settingUp || busy"
              @click="setUpDashboard"
            >
              <fts-spinner v-if="dashboardBusy" :size="14" />
              {{ dashboardBusy ? "Setting up the dashboard…" : "Set up the dashboard again" }}
            </button>
            <button
              type="button"
              class="fts-settings-btn"
              :disabled="settingUp || busy || cannotReach"
              @click="runSetup"
            >
              <fts-spinner v-if="settingUp && !runningAction" :size="14" />
              {{ settingUp && !runningAction ? "Running the setup…" : "Run the setup again" }}
            </button>
          </div>
        </section>

        <!-- KY 2026-10-24: a book starts read-only. The admin makes it writable here, and tests writes
             without saving anything. The PC's owner keeps a switch of their own in the connector. -->
        <section
          class="fts-settings-card fts-autocount-writes"
          aria-labelledby="fts-autocount-writes"
          v-if="writesCard"
        >
          <div>
            <h2 id="fts-autocount-writes">
              Writes to AutoCount
              <span class="fts-autocount-badge" :class="{ 'is-on': writesCard.on }">
                <i aria-hidden="true"></i>{{ writesCard.on ? "On" : "Read-only" }}
              </span>
            </h2>
            <p class="fts-settings-card-sub">{{ writesCard.sub }}</p>
            <p class="fts-autocount-hint" v-if="writesCard.changed">{{ writesCard.changed }}</p>
            <!-- What writes cost (activation fee doc §8 W-1, SVN), and the way back to read-only. -->
            <p class="fts-autocount-hint fts-autocount-writes-plan" v-if="writesPlan">
              {{ writesPlan.note }}
              <button
                v-if="writesPlan.readOnly"
                type="button"
                class="fts-autocount-link fts-autocount-writes-downgrade"
                :disabled="Boolean(writesBusy) || busy"
                @click="switchToReadOnly"
              >
                {{ writesBusy === "plan" ? "Switching…" : "Switch to read-only: " + writesPlan.readOnly }}
              </button>
            </p>
          </div>
          <div class="fts-autocount-manage-foot">
            <button
              type="button"
              class="fts-settings-btn fts-autocount-writes-switch"
              :class="{ 'is-primary': !writesCard.on }"
              :disabled="Boolean(writesBusy) || busy || settingUp"
              @click="setWrites(!writesCard.on)"
            >
              <template v-if="writesBusy === 'set'">{{ writesCard.on ? "Turning off…" : "Turning on…" }}</template>
              <template v-else>{{ writesCard.on ? "Turn writes off" : "Turn writes on" }}</template>
            </button>
            <button
              type="button"
              class="fts-settings-btn fts-autocount-writes-test"
              :disabled="Boolean(writesBusy) || busy"
              @click="testWrites"
            >
              <fts-spinner v-if="writesBusy === 'test'" :size="14" />
              {{ writesBusy === "test" ? "Testing…" : "Test writes" }}
            </button>
          </div>
          <template v-if="writesTest">
            <p class="fts-autocount-hint fts-autocount-writes-verdict">{{ writesTest.verdict }}</p>
            <ul class="fts-autocount-writes-checks" aria-label="What a write passes">
              <li v-for="check in writesTest.checks" :key="check.key" :class="'is-' + check.state">
                <font-awesome-icon :icon="['fas', check.icon]" aria-hidden="true" />
                <span><strong>{{ check.label }}</strong> {{ check.detail }}</span>
              </li>
            </ul>
            <ul class="fts-autocount-writes-checks" aria-label="Modules that can write" v-if="writesTest.modules.length">
              <li v-for="row in writesTest.modules" :key="row.key" :class="'is-' + row.state">
                <font-awesome-icon :icon="['fas', row.icon]" aria-hidden="true" />
                <span><strong>{{ row.name }}</strong> {{ row.detail }}</span>
              </li>
            </ul>
          </template>
          <p class="fts-settings-note is-error fts-autocount-writes-error" role="alert" v-if="writesError">
            {{ writesError }}
          </p>
        </section>

        <!-- Ready-made workflows (docs/integration-autocount-templates-proposal.md §4, SVN): modules of
             Inistate's own, built on the AutoCount ones. -->
        <section
          class="fts-settings-card fts-autocount-workflows"
          aria-labelledby="fts-autocount-workflows"
          v-if="workflowCards.length"
        >
          <div>
            <h2 id="fts-autocount-workflows">Ready-made workflows</h2>
            <p class="fts-settings-card-sub">
              Built on your AutoCount modules. Adding one creates its own Inistate modules; nothing in AutoCount
              changes.
            </p>
          </div>

          <div class="fts-autocount-workflow" v-for="flow in workflowCards" :key="flow.key">
            <div class="fts-autocount-workflow-head">
              <span class="fts-autocount-workflow-text">
                <strong>
                  <font-awesome-icon v-if="flow.locked" :icon="['fas', 'lock']" class="fts-autocount-workflow-lock" />
                  {{ flow.name }}
                </strong>
                <span v-if="flow.summary">{{ flow.summary }}</span>
                <span v-if="flow.locked && !flow.added">{{ lockedHint }}.</span>
              </span>
              <span class="fts-autocount-badge is-on" v-if="flow.added">
                <i aria-hidden="true"></i>Added
              </span>
              <!-- Locked on Free: the way to it is the trial (or buying), not an Add that the server refuses. -->
              <button
                v-else-if="flow.locked && flow.unlock === 'trial'"
                type="button"
                class="fts-settings-btn is-primary fts-autocount-workflow-trial"
                :disabled="Boolean(planBusy) || Boolean(addingWorkflow) || busy"
                @click="startTrial"
              >
                {{ planBusy === "trial" ? "Starting…" : "Start trial to add" }}
              </button>
              <button
                v-else-if="flow.locked && flow.unlock === 'buy'"
                type="button"
                class="fts-settings-btn is-primary fts-autocount-workflow-buy"
                :disabled="Boolean(planBusy) || Boolean(addingWorkflow) || busy"
                @click="buyFull"
              >
                {{ planBusy === "buy" ? "Opening…" : "Activate to add" }}
              </button>
              <button
                v-else-if="!flow.locked"
                type="button"
                class="fts-settings-btn is-primary fts-autocount-workflow-add"
                :disabled="Boolean(addingWorkflow) || busy"
                @click="addWorkflow(flow)"
              >
                {{ addingWorkflow === flow.key ? "Adding…" : "Add" }}
              </button>
            </div>

            <p class="fts-autocount-workflow-reads" v-if="flow.reads.length">
              Works from:
              <span
                v-for="read in flow.reads"
                :key="read.key"
                class="fts-autocount-workflow-read"
                :class="{ 'is-missing': read.missing }"
              >{{ read.text }}</span>
            </p>

            <template v-if="flow.added">
              <div class="fts-autocount-workflow-links" v-if="flow.links.length">
                <router-link
                  v-for="link in flow.links"
                  :key="link.key"
                  :to="link.route"
                  class="fts-autocount-workflow-link"
                >Open {{ link.name }}</router-link>
              </div>
              <p class="fts-autocount-hint fts-autocount-p" v-if="flow.next">{{ flow.next }}</p>
            </template>

            <p class="fts-settings-note is-error fts-autocount-workflow-error" role="alert" v-if="flow.error">
              {{ flow.error }}
            </p>
          </div>
        </section>

        <section class="fts-autocount-ask" aria-labelledby="fts-autocount-ask-home">
          <h2 id="fts-autocount-ask-home">
            <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
            Try asking GNey
          </h2>
          <p v-for="question in questions" :key="question">&ldquo;{{ question }}&rdquo;</p>
        </section>

        <section class="fts-autocount-other" aria-labelledby="fts-autocount-other">
          <font-awesome-icon :icon="['fas', 'info-circle']" class="fts-autocount-other-icon" />
          <div>
            <h2 id="fts-autocount-other">Another company&rsquo;s account book?</h2>
            <p>
              Each account book has its own workspace. Create one, open Settings &rsaquo; AutoCount there,
              and join the same connector: it can serve several workspaces, each seeing only its own book.
            </p>
          </div>
        </section>

        <div class="fts-autocount-danger">
          <!-- The quiet way to stop it for a while; Disconnect is the one that forgets the PC. -->
          <button
            v-if="!turnedOff"
            type="button"
            class="fts-settings-btn fts-autocount-turn-off"
            :disabled="busy || settingUp"
            @click="turnOff"
          >
            {{ switching ? "Turning off…" : "Turn off" }}
          </button>
          <button
            type="button"
            class="fts-settings-btn is-danger"
            :disabled="busy || settingUp"
            @click="disconnect"
          >
            {{ disconnecting ? "Disconnecting…" : "Disconnect AutoCount" }}
          </button>
        </div>
      </template>

      <!-- ===== the three steps ===== -->
      <div class="fts-autocount-layout" v-else>
        <div class="fts-autocount-main">
          <div class="fts-autocount-progress">
            <div class="fts-autocount-progress-text">
              <strong>Step {{ stepNumber }} of 3</strong>
              <span>About 5 minutes, with whoever looks after the AutoCount server</span>
            </div>
            <div
              class="fts-autocount-progress-track"
              role="progressbar"
              aria-label="Setup progress"
              aria-valuemin="0"
              aria-valuemax="3"
              :aria-valuenow="stepNumber - 1"
            >
              <div class="fts-autocount-progress-fill" :style="{ width: progressPct }"></div>
            </div>
          </div>

          <ol class="fts-autocount-steps" aria-label="Set up AutoCount">
            <!-- 1. Install the connector and join -->
            <li class="fts-autocount-step" :class="stepClass(1)" :aria-current="stepNumber === 1 ? 'step' : null">
              <span class="fts-autocount-step-mark">
                <font-awesome-icon v-if="stepNumber > 1" :icon="['fas', 'check']" />
                <template v-else>1</template>
              </span>
              <div class="fts-autocount-step-body">
                <div class="fts-autocount-step-head" v-if="stepNumber > 1">
                  <strong>Connector joined: {{ machineName }}<span class="fts-autocount-sr">, done</span></strong>
                  <span v-if="agentOnline">
                    <span class="fts-autocount-badge is-on"><i aria-hidden="true"></i>Online</span>
                  </span>
                  <span v-else-if="lastContact">Last contact {{ lastContact }}</span>
                </div>

                <template v-else>
                  <div class="fts-autocount-step-head">
                    <strong>Install the connector and join</strong>
                    <span>On the AutoCount server: install it, type the code, and pick this workspace&rsquo;s account book there.</span>
                  </div>

                  <div class="fts-autocount-download">
                    <a class="fts-settings-btn" :href="downloadUrl" download>
                      <font-awesome-icon :icon="['fas', 'download']" />
                      Download for Windows
                    </a>
                    <span class="fts-autocount-hint">
                      Needs .NET Framework 4.8 on the PC, and installs as a Windows service. Already installed for
                      another workspace? Use it again.
                    </span>
                  </div>

                  <p class="fts-autocount-p">Open it and type this code:</p>

                  <div class="fts-autocount-code" :class="{ 'is-expired': codeExpired }" v-if="code">
                    <code aria-label="Pairing code">{{ code }}</code>
                    <span class="fts-autocount-code-actions">
                      <button type="button" class="fts-settings-btn" :disabled="codeExpired" @click="copyCode">
                        <font-awesome-icon :icon="['far', 'clone']" />
                        {{ copied ? "Copied" : "Copy" }}
                      </button>
                      <a href="#" class="fts-autocount-new-code" @click.prevent="newCode">New code</a>
                    </span>
                  </div>
                  <div class="fts-settings-loading" v-else-if="pairingBusy">
                    <fts-spinner :size="16" />
                    <span>Making a code&hellip;</span>
                  </div>
                  <div class="fts-autocount-code is-empty" v-else>
                    <span>No code right now.</span>
                    <button type="button" class="fts-settings-btn is-primary" @click="newCode">New code</button>
                  </div>

                  <p class="fts-autocount-hint fts-autocount-p">
                    On the PC you then pick the account book this workspace may use. The connector gets read-only
                    access to that book alone.
                  </p>

                  <div class="fts-autocount-wait">
                    <span class="fts-autocount-status">
                      <fts-spinner :size="16" />
                      <span role="status" aria-live="polite">Waiting for the connector&hellip;</span>
                      <span class="fts-autocount-hint" v-if="expiresIn">code expires in {{ expiresIn }}</span>
                      <span class="fts-autocount-hint is-warn-text" v-else-if="codeExpired">this code has expired</span>
                    </span>
                    <a :href="mailtoHref" v-if="mailtoHref">Send these steps to your IT person</a>
                  </div>
                </template>
              </div>
            </li>

            <!-- 2. Approve the PC -->
            <li class="fts-autocount-step" :class="stepClass(2)" :aria-current="stepNumber === 2 ? 'step' : null">
              <span class="fts-autocount-step-mark">
                <font-awesome-icon v-if="stepNumber > 2" :icon="['fas', 'check']" />
                <template v-else>2</template>
              </span>
              <div class="fts-autocount-step-body">
                <div class="fts-autocount-step-head" v-if="stepNumber > 2">
                  <strong>PC approved<span class="fts-autocount-sr">, done</span></strong>
                  <span>
                    <template v-if="fingerprint">Fingerprint <span class="fts-autocount-mono">{{ fingerprint }}</span>, with</template><template v-else>With</template>
                    the book {{ bookName }}.<template v-if="agent && agent.approvedBy"> Approved by {{ agent.approvedBy }}.</template>
                  </span>
                </div>

                <template v-else-if="stepNumber === 2">
                  <div class="fts-autocount-step-head">
                    <strong>Approve {{ machineName }}</strong>
                    <span>Approve only if this matches the connector window on the PC.</span>
                  </div>

                  <dl class="fts-autocount-check">
                    <dt>Fingerprint</dt>
                    <dd>
                      <span class="fts-autocount-fingerprint" role="group" :aria-label="'Fingerprint ' + fingerprint">
                        <span v-for="(group, index) in fingerprintGroups" :key="index">{{ group }}</span>
                      </span>
                    </dd>
                    <dt>Account book</dt>
                    <dd>
                      <template v-if="hasBook">
                        <strong>{{ bookName }}</strong>
                        <span class="fts-autocount-hint">
                          <span class="fts-autocount-mono">{{ book.database }}</span> on {{ book.server }}<template v-if="book.version"> &middot; AutoCount {{ book.version }}</template>
                        </span>
                      </template>
                      <span v-else>
                        Not picked yet. Join the connector again from the PC and pick this workspace&rsquo;s book.
                      </span>
                    </dd>
                    <dt>Reads as</dt>
                    <dd>{{ readsAs }}</dd>
                  </dl>

                  <p class="fts-autocount-hint fts-autocount-p">
                    Wrong book? It is picked on the PC: Inistate Connector &rsaquo; Workspaces &rsaquo; {{ workspaceName }}.
                  </p>

                  <div class="fts-autocount-actions">
                    <button
                      type="button"
                      class="fts-settings-btn is-danger fts-autocount-reject"
                      :disabled="busy || settingUp"
                      @click="reject"
                    >
                      {{ rejecting ? "Forgetting it…" : "Not our PC" }}
                    </button>
                    <button
                      type="button"
                      class="fts-settings-btn is-primary fts-autocount-approve"
                      :disabled="busy || settingUp || !hasBook || Boolean(connectorsOff) || noModuleChosen"
                      @click="approve"
                    >
                      {{ approving ? "Approving…" : "Approve and set up" }}
                    </button>
                  </div>
                </template>

                <div class="fts-autocount-step-head" v-else>
                  <strong>Approve the PC</strong>
                  <span>Check its fingerprint and the account book it offers, then approve.</span>
                </div>
              </div>
            </li>

            <!-- 3. Inistate sets it up -->
            <li class="fts-autocount-step" :class="stepClass(3)" :aria-current="stepNumber === 3 ? 'step' : null">
              <span class="fts-autocount-step-mark">3</span>
              <div class="fts-autocount-step-body">
                <template v-if="stepNumber === 3">
                  <div class="fts-autocount-step-head">
                    <strong>{{ isFailed ? "The setup stopped" : "Inistate is setting it up" }}</strong>
                    <span v-if="!isFailed">About a minute. You can leave this page; it carries on.</span>
                  </div>

                  <div class="fts-settings-banner is-warn fts-autocount-failure" v-if="isFailed">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
                    <span>
                      <strong>{{ failureMessage }}</strong>
                      <template v-if="reachHint">Check that the Inistate Connector is running on the PC.</template>
                    </span>
                  </div>

                  <!-- Above the checklist, which can be long: the way on should not need a scroll. -->
                  <div class="fts-autocount-retry-row" v-if="isFailed || (state === 'setup' && !settingUp)">
                    <button
                      type="button"
                      class="fts-settings-btn is-primary fts-autocount-retry"
                      :disabled="settingUp || busy || cannotReach"
                      @click="runSetup"
                    >
                      <template v-if="isFailed">{{ settingUp ? "Trying again…" : "Try again" }}</template>
                      <template v-else>Start the setup</template>
                    </button>
                  </div>

                  <ul class="fts-autocount-checklist" aria-label="Setup progress" v-if="checklist.length">
                    <li v-for="row in checklist" :key="row.key" :class="'is-' + row.mark">
                      <span class="fts-autocount-row-mark">
                        <fts-spinner v-if="row.mark === 'running'" :size="16" />
                        <font-awesome-icon v-else-if="row.mark === 'done'" :icon="['fas', 'check-circle']" />
                        <font-awesome-icon v-else-if="row.mark === 'failed'" :icon="['fas', 'times-circle']" />
                        <font-awesome-icon v-else-if="row.mark === 'skipped'" :icon="['fas', 'ban']" />
                        <font-awesome-icon v-else-if="row.mark === 'locked'" :icon="['fas', 'lock']" />
                        <i v-else class="fts-autocount-hollow"></i>
                      </span>
                      <span class="fts-autocount-row-text">
                        <span class="fts-autocount-row-label">{{ row.text }}<span class="fts-autocount-sr">, {{ row.mark }}</span></span>
                        <span class="fts-autocount-row-detail" v-if="row.detail">{{ row.detail }}</span>
                      </span>
                    </li>
                  </ul>
                  <div class="fts-settings-loading" v-else-if="settingUp">
                    <fts-spinner :size="16" />
                    <span>Starting the setup&hellip;</span>
                  </div>

                  <p class="fts-autocount-sr" role="status" aria-live="polite">{{ liveStatus }}</p>

                  <p class="fts-autocount-hint fts-autocount-p">
                    If a check fails, it stops before creating anything and says what to fix.
                  </p>
                  <p class="fts-autocount-p" v-if="!isFailed">This page becomes your AutoCount home when it finishes.</p>
                </template>

                <div class="fts-autocount-step-head" v-else>
                  <strong>Inistate sets it up</strong>
                  <span v-if="hasModuleList">Checks the book, then creates the modules ticked under What you get.</span>
                  <span v-else>Checks the book, then creates your AutoCount modules. Nothing to choose.</span>
                </div>
              </div>
            </li>
          </ol>
        </div>

        <aside class="fts-autocount-aside" aria-label="What you get">
          <section class="fts-autocount-get" aria-labelledby="fts-autocount-get">
            <h2 id="fts-autocount-get">What you get</h2>
            <template v-if="hasModuleList">
              <p>
                Created for you once the PC is approved, read live from your book. Untick what this workspace does
                not need; you can add it later.
              </p>
              <!-- Before the trial the modules start unticked: a setup then would only report them locked. -->
              <p v-if="planOnFree && plan.trialAvailable">Every module is included in the 14-day free trial.</p>
              <ul class="fts-autocount-get-pick">
                <li v-for="m in shownModules" :key="m.key">
                  <label :for="'fts-ac-get-' + m.key">
                    <input
                      type="checkbox"
                      :id="'fts-ac-get-' + m.key"
                      :value="m.key"
                      v-model="setupChoice"
                      :disabled="settingUp"
                    />
                    <span>{{ m.name }}</span>
                  </label>
                  <span class="fts-autocount-get-full" v-if="isLocked(m)">{{ lockedHint }}</span>
                </li>
              </ul>
              <p class="fts-autocount-hint" role="status" v-if="noModuleChosen">Choose at least one module.</p>
              <div v-if="asideTrial">
                <button
                  type="button"
                  class="fts-settings-btn fts-autocount-get-trial"
                  :disabled="Boolean(planBusy) || settingUp"
                  @click="startTrial"
                >
                  {{ planBusy === "trial" ? "Starting…" : "Start 14-day free trial" }}
                </button>
              </div>
            </template>
            <template v-else>
              <p>Created for you once the PC is approved, read live from your book:</p>
              <ul v-if="templateModules.length">
                <li v-for="name in templateModules" :key="name">{{ name }}</li>
              </ul>
            </template>
          </section>

          <section class="fts-autocount-ask" aria-labelledby="fts-autocount-ask">
            <h2 id="fts-autocount-ask">
              <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
              Then ask GNey
            </h2>
            <p v-for="question in questions" :key="question">&ldquo;{{ question }}&rdquo;</p>
          </section>

          <section class="fts-autocount-safe" aria-label="Safe by default">
            <span><font-awesome-icon :icon="['fas', 'lock']" />Read only: nothing in AutoCount changes.</span>
            <span><font-awesome-icon :icon="['fas', 'plug']" />Nothing to open in your firewall.</span>
            <span><font-awesome-icon :icon="['fas', 'key']" />Any password is sealed so only your connector can read it.</span>
          </section>
        </aside>
      </div>
    </template>

    <!-- The activation sheet (docs/billing-autocount-activation-fee-proposal.md §3, SVN): what keeping
         or activating AutoCount Full costs and when, before Stripe. -->
    <div class="fts-autocount-sheet-overlay" v-if="sheetView" @mousedown.self="closeSheet">
      <div
        class="fts-autocount-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fts-autocount-sheet-title"
        ref="sheet"
        @keydown.esc.stop="closeSheet"
        @keydown.tab="trapSheetFocus"
      >
        <div class="fts-autocount-sheet-head">
          <div>
            <h2 id="fts-autocount-sheet-title">{{ sheetView.title }}</h2>
            <p>{{ sheetView.intro }}</p>
          </div>
          <button
            type="button"
            class="fts-autocount-sheet-close"
            aria-label="Close"
            :disabled="sheet.busy === 'pay'"
            @click="closeSheet"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <!-- Read-only or with writes to the book (§8 W-1): each a whole price, one replaces the other. -->
        <fieldset class="fts-autocount-sheet-lines-choice" v-if="sheetView.choices.length">
          <legend>Choose your plan</legend>
          <label
            v-for="c in sheetView.choices"
            :key="String(c.writes)"
            class="fts-autocount-choice"
            :class="{ 'is-on': c.on }"
          >
            <input
              type="radio"
              name="fts-autocount-line"
              :value="c.writes"
              :checked="c.on"
              :disabled="Boolean(sheet.busy)"
              @change="chooseLine(c.writes)"
            />
            <span class="fts-autocount-choice-text">
              <strong>{{ c.label }}</strong>
              <span>{{ c.detail }}</span>
            </span>
            <strong class="fts-autocount-choice-price">{{ c.price }}</strong>
          </label>
        </fieldset>

        <fieldset class="fts-autocount-sheet-country" v-if="sheetView.countries.length">
          <legend>Where is your business registered?</legend>
          <div class="fts-autocount-seg">
            <label
              v-for="c in sheetView.countries"
              :key="c.region"
              class="fts-autocount-seg-option"
              :class="{ 'is-on': c.on }"
            >
              <input
                type="radio"
                name="fts-autocount-country"
                :value="c.region"
                :checked="c.on"
                :disabled="Boolean(sheet.busy)"
                @change="pickCountry(c.region)"
              />
              <span>{{ c.label }}</span>
            </label>
          </div>
          <p class="fts-autocount-hint">
            {{ sheet.busy === "region" ? "Updating the prices…" : "Sets the currency for this workspace. Fixed after your first payment." }}
          </p>
        </fieldset>
        <p class="fts-autocount-hint" v-else-if="sheetView.confirmedLine">{{ sheetView.confirmedLine }}</p>

        <table class="fts-autocount-sheet-lines" v-if="sheetView.lines.length">
          <tbody>
            <tr v-for="line in sheetView.lines" :key="line.key">
              <th scope="row">{{ line.label }}</th>
              <td>{{ line.value }}</td>
            </tr>
          </tbody>
        </table>

        <div class="fts-autocount-sheet-due" v-if="sheetView.due.length">
          <div
            v-for="row in sheetView.due"
            :key="row.key"
            class="fts-autocount-sheet-due-row"
            :class="{ 'is-strong': row.strong }"
          >
            <span>{{ row.label }}</span>
            <span>{{ row.value }}</span>
          </div>
        </div>
        <p class="fts-autocount-hint" v-else-if="sheet.busy === 'preview'">Asking Stripe what it would invoice…</p>

        <div class="fts-settings-banner is-warn" role="alert" v-if="sheet.error || sheetView.unsold">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
          <span>{{ sheet.error || sheetView.unsold }}</span>
        </div>

        <p class="fts-autocount-hint">
          Prices exclude tax.
          <template v-if="sheetView.activation">Activation is charged once for this workspace.</template>
          Cancel any time from Billing &amp; invoices; nothing is deleted.
        </p>

        <div class="fts-autocount-sheet-foot">
          <button type="button" class="fts-settings-btn" :disabled="sheet.busy === 'pay'" @click="closeSheet">
            Not now
          </button>
          <button
            type="button"
            class="fts-settings-btn is-primary fts-autocount-sheet-pay"
            :disabled="!sheetView.canPay && sheet.busy !== 'pay'"
            :aria-disabled="String(sheet.busy === 'pay')"
            @click="confirmSheet"
          >
            {{ sheetView.primary }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Settings › AutoCount (docs/integration-data-sources-ux-proposal.md §3.1 in the SVN repo).
 *
 * One account book per workspace, in three steps: the admin hands a one-time code to whoever
 * installs the Inistate Connector on the AutoCount server, approves that PC after comparing its
 * fingerprint, and Inistate then checks the book and creates the read-only AutoCount modules by
 * itself. Once that is done the page is the AutoCount home.
 *
 * The server says which of those the workspace is at (`state`), and every answer is the whole
 * page, so the page only ever replaces what it shows with the latest answer. While something can
 * still change on its own (a connector joining, a setup running) it asks again every few seconds.
 *
 * Two answers can cross: a poll sent before an action lands may come back after it, with the
 * state from before. Each action's answer therefore moves `generation` on, and a poll that left
 * under an older generation is dropped.
 */
import commons from "../../commons/commons";
import { clientStore } from "../../store/clientStore";
import FTSSpinner from "../../elements/FTSSpinner";
import api from "../sourceConnectionsApi";
import { timeAgo, countdown, clockTime, seenWithin, shortDate } from "../sourceTime";
import { clearBillingReturn, readBillingReturn } from "../../commons/billing";

/**
 * The connector's Windows installer, the MSI, signed by Inistate Pte Ltd. One address for every Inistate server: the
 * Jenkins job "Deploy Production Connector" builds, signs and puts each release there.
 */
const CONNECTOR_DOWNLOAD_URL = "https://e.inistate.com/connector/InistateConnector.msi";

const POLL_MS = 3000;
// While this page's own setup request is in flight, so the checklist moves as it happens.
const SETUP_POLL_MS = 1500;
// The states something else can move on from: a connector joining, a setup running.
const POLLED_STATES = ["start", "approve", "setup", "setting-up"];
// A connector that called in this recently is shown online even before its connection exists.
const ONLINE_WINDOW_MS = 60 * 1000;
// A connector turned back on asks again within a minute; the page waits a little longer than that.
const TURN_ON_WAIT_MS = 90 * 1000;
// Back from AutoCount Full's Checkout: how long the page waits for Stripe's webhook to land.
const CONFIRM_WAIT_MS = 30 * 1000;

const UNAVAILABLE =
  "AutoCount is not on this environment yet. This page works once it runs an API that has it.";

const QUESTIONS = [
  "What has our biggest customer been buying lately?",
  "Which items should we reorder this week?",
  "Who are our top customers this quarter?",
];

// The checklist's words for each check, by its state.
const STEP_TEXT = {
  reach: {
    waiting: "Reach the connector",
    running: "Reaching the connector…",
    done: "Reached the connector",
    failed: "Could not reach the connector",
  },
  open: {
    waiting: "Open the book",
    running: "Opening the book…",
    done: "Opened the book",
    failed: "Could not open the book",
  },
  // A sign-in that can also change data no longer stops the setup: the read-only modules and the
  // connector are the guard. The server says in its detail what this sign-in may do; "failed" is
  // only an older server's.
  readOnly: {
    waiting: "Check the connector's sign-in",
    running: "Checking the connector's sign-in…",
    done: "Checked the connector's sign-in",
    failed: "It can change data",
  },
  tables: {
    waiting: "Check the tables the modules need",
    running: "Checking the tables the modules need…",
    done: "Every table the modules need is there",
    failed: "The tables the modules need are not there",
  },
  modules: {
    failed: "The modules could not be created",
  },
};

const MARKS = { done: "done", running: "running", failed: "failed", waiting: "waiting" };

const STOPPED = "Stopped here";

/**
 * One check of the setup as a checklist row, or null for one the module rows stand for. A run the
 * server gave up on says "stopped" where it was; an older server still says "running" there, so a
 * run that `stopped` (the page is in "failed") reads that as stopped too.
 */
function stepRow(step, stopped) {
  // The modules step is the module rows that follow it; it only needs a row of its own to fail.
  if (step.key === "modules" && step.state !== "failed") return null;
  const words = STEP_TEXT[step.key] || {};
  if (step.state === "stopped" || (stopped && step.state === "running")) {
    return { key: "step-" + step.key, mark: "waiting", text: words.waiting || step.key, detail: STOPPED };
  }
  const state = MARKS[step.state] ? step.state : "waiting";
  let text = words[state] || words.waiting || step.detail || step.key;
  let detail = "";
  if (state === "failed") {
    detail = step.detail || "";
  } else if (state === "done") {
    // The tables check says in its own words what it found ("5 of 7 modules can be made").
    if (step.key === "tables") text = step.detail || text;
    else detail = step.detail || "";
  }
  return { key: "step-" + step.key, mark: MARKS[state], text, detail };
}

/** One module of the setup as a checklist row. */
function moduleRow(module, index, stopped) {
  const name = module.name || module.template || "A module";
  const key = "module-" + (module.template || index);
  const reason = module.reason || "";
  if (module.state === "stopped" || (stopped && module.state === "running")) {
    return { key, mark: "waiting", text: name, detail: STOPPED };
  }
  switch (module.state) {
    // SS06315: a module made without fields the book cannot feed (an older AutoCount) says which.
    case "created":
      return { key, mark: "done", text: "Created " + name, detail: reason };
    case "linked":
      return { key, mark: "done", text: name + " was made before: linked" };
    case "updated":
      return { key, mark: "done", text: "Updated " + name + " to the latest design", detail: reason };
    case "notSelected":
      return { key, mark: "skipped", text: name + ": not chosen", detail: "Add it from the AutoCount page when it is needed." };
    case "running":
      return { key, mark: "running", text: "Creating " + name + "…" };
    case "skipped":
      return { key, mark: "skipped", text: "Skipped " + name, detail: reason };
    case "locked":
      // Not on this workspace's plan: nothing was made, and the run carried on without it.
      return { key, mark: "locked", text: name + ": part of AutoCount Full", detail: reason };
    case "failed":
      return { key, mark: "failed", text: name + " could not be created", detail: failureReason(reason) };
    default:
      // waiting, and notStarted after a check failed: the banner above already says it stopped.
      return { key, mark: "waiting", text: name };
  }
}

/** "Could not be created: boom" under a row that already says it could not be created. */
function failureReason(reason) {
  return String(reason || "").replace(/^could not be (created|updated):\s*/i, "");
}

/** The modules of a setup run's answer. */
function runModules(res) {
  const setup = res && res.data && res.data.setup;
  return setup && Array.isArray(setup.modules) ? setup.modules.filter(Boolean) : [];
}

// ---- SS06269: setting the dashboard up again ------------------------------------------------------

/** How many of each the server counted in `dashboard`: { added, replaced, removed }. */
function dashboardCounts(dashboard) {
  const count = (key) => Math.max(0, Number(dashboard && dashboard[key]) || 0);
  return { added: count("added"), replaced: count("replaced"), removed: count("removed") };
}

/** What replacing does, for the question asked before it. */
function dashboardReplaceText(dashboard) {
  const n = dashboardCounts(dashboard);
  const parts = [];
  if (n.replaced) {
    parts.push(n.replaced === 1
      ? "1 AutoCount widget on the dashboard was changed and goes back to the template's design."
      : n.replaced + " AutoCount widgets on the dashboard were changed and go back to the template's design.");
  }
  if (n.removed) {
    parts.push(n.removed + " from an earlier AutoCount template " + (n.removed === 1 ? "is" : "are") + " removed.");
  }
  if (n.added) {
    parts.push(n.added === 1 ? "1 missing one is added." : n.added + " missing ones are added.");
  }
  parts.push("Widgets you made yourself stay, and nothing in AutoCount changes.");
  return parts.join(" ");
}

/** What was done, for the toast. */
function dashboardDoneText(dashboard) {
  const n = dashboardCounts(dashboard);
  if (!n.added && !n.replaced && !n.removed) return "The dashboard already has the AutoCount widgets";
  if (!n.replaced && !n.removed) {
    return n.added + " AutoCount " + (n.added === 1 ? "widget" : "widgets") + " added to the dashboard";
  }
  const parts = [];
  if (n.added) parts.push(n.added + " added");
  if (n.replaced) parts.push(n.replaced + " replaced");
  if (n.removed) parts.push(n.removed + " removed");
  return "AutoCount dashboard set up: " + parts.join(", ");
}

// ---- Writes to the book (KY 2026-10-24: a book starts read-only) ---------------------------------

/** The switches a write passes, in the server's order, as the test lists them. */
const WRITE_CHECKS = {
  server: "Inistate",
  plan: "Plan",
  book: "This book",
  connector: "Connector",
  pc: "The PC",
  signIn: "AutoCount sign-in",
};

/** A check's state: on ("ok"), off ("off"), or not known ("unknown", e.g. a connector too old to say). */
function checkState(ok) {
  if (ok === true) return { state: "ok", icon: "check-circle" };
  if (ok === false) return { state: "off", icon: "times-circle" };
  return { state: "unknown", icon: "minus-circle" };
}

/** The test's answer as the card shows it: a verdict, each switch, and each module that can write. */
function writesTestView(test) {
  const checks = (test.checks || []).map((check) => ({
    key: check.key,
    label: WRITE_CHECKS[check.key] || check.key,
    detail: check.detail || "",
    ...checkState(check.ok),
  }));
  const modules = (test.modules || []).map((row) => ({
    key: row.key,
    name: row.name,
    detail: row.detail || (row.ok ? "Can write." : ""),
    ...checkState(row.ok),
  }));
  const unknown = checks.some((check) => check.state === "unknown");
  return {
    checks,
    modules,
    verdict: test.ready
      ? "Ready: writes reach AutoCount" + (unknown ? ", as far as could be checked." : ".") + " Nothing was saved."
      : "Not ready: a switch below is off. Nothing was saved.",
  };
}

// ---- AutoCount Full (docs/billing-autocount-connector-pricing-proposal.md §9, SVN) ----------------

// The activation fee (docs/billing-autocount-activation-fee-proposal.md §3, SVN): the pages show no
// free tier (A-1), so a module that is not Full yet reads as included in the trial, or locked.

const CURRENCY_SIGNS = { myr: "RM", sgd: "S$", usd: "US$" };

const CURRENCY_NAMES = { myr: "Malaysian ringgit (RM)", sgd: "Singapore dollars (S$)", usd: "US dollars (US$)" };

/** The business countries a workspace admin picks from before the first payment (A-5a). */
const BILLING_COUNTRIES = [
  { region: "MY", label: "Malaysia" },
  { region: "SG", label: "Singapore" },
  { region: "US", label: "Other countries" },
];

/** Choosing the country's refusals when the server sends a code without words. */
const REGION_REFUSALS = {
  region_confirmed: "The first payment fixed this workspace's currency. Talk to us if it needs to change.",
  dealer_billed: "Your vendor bills this workspace, so its currency is set with them.",
  bad_region: "Choose Malaysia, Singapore or Other countries.",
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/** The trial's refusals when the server sends a code without words. */
const TRIAL_REFUSALS = {
  trial_used: "This workspace has already had its free trial.",
  no_book: "Approve the AutoCount book first; the trial starts once it is connected.",
  trial_not_needed: "AutoCount Full is already on for this workspace.",
};

/** Buying's refusals (api/billing/connectors/autocount/*) when the server sends a code without words. */
const BUY_REFUSALS = {
  dealer_billed: "Your vendor bills this workspace: ask them for AutoCount Full.",
  connector_active: "AutoCount Full is already on this workspace's subscription.",
  price_not_configured: "AutoCount Full cannot be bought online here yet. Talk to us and we will set it up.",
  not_enabled: "AutoCount Full cannot be bought online here yet. Talk to us and we will set it up.",
};

/** "RM61.20", "US$30": whole amounts drop the cents, the way a price list reads. */
function moneyText(amount, currency) {
  const n = Number(amount);
  if (!isFinite(n)) return "";
  const code = String(currency || "").toLowerCase();
  const sign = Object.prototype.hasOwnProperty.call(CURRENCY_SIGNS, code)
    ? CURRENCY_SIGNS[code]
    : (code ? code.toUpperCase() + " " : "");
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  const digits = whole ? 0 : 2;
  return sign + n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** "RM99/month" from the plan's { amount, currency, interval }; "" when there is no price. */
function priceText(price) {
  if (!price || !(Number(price.amount) > 0)) return "";
  return moneyText(price.amount, price.currency) + "/" + (price.interval === "year" ? "year" : "month");
}

/** "RM499" when buying now owes the one-time activation; "" when it is paid, waived or not set up. */
function activationText(plan) {
  const activation = plan && plan.activation;
  if (!plan || !plan.activationDue || !activation || !(Number(activation.amount) > 0)) return "";
  return moneyText(activation.amount, activation.currency);
}

/** "RM499 activation + RM99/month", "RM99/month" without an activation owed, "" without a price. */
function offerText(plan) {
  const price = priceText(plan && plan.price);
  if (!price) return "";
  const activation = activationText(plan);
  return activation ? activation + " activation + " + price : price;
}

function closedSheet() {
  return { open: false, mode: "buy", writes: false, preview: null, busy: "", error: "" };
}

/** Same keys, in any order. */
function sameKeys(a, b) {
  return a.length === b.length && a.every((key) => b.indexOf(key) >= 0);
}

export default {
  name: "SettingsAutoCount",
  components: { "fts-spinner": FTSSpinner },
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loading: true,
      unavailable: "",
      error: "",
      // A refresh that failed says so until the next one works; an action's error stays.
      errorFromPoll: false,
      view: null,
      now: Date.now(),
      approving: false,
      rejecting: false,
      disconnecting: false,
      settingUp: false,
      pairingBusy: false,
      codeRequested: false,
      autoSetupStarted: false,
      copied: false,
      // Turning the connector off or on (the request itself).
      switching: false,
      // Turned back on, and waiting for the connector to check in (at most TURN_ON_WAIT_MS).
      turningOn: false,
      turnOnTimedOut: false,
      // The ready-made workflow being added, and what went wrong per workflow, by key.
      addingWorkflow: "",
      workflowErrors: {},
      // The modules chosen before the first setup (null: all of them), the ones ticked on the home
      // to add, the module being shown or hidden, what the running setup is for ("add", "update",
      // or "" for a plain run), and the modules it is updating.
      setupPicked: null,
      picked: [],
      moduleBusy: "",
      runningAction: "",
      updatingKeys: [],
      // SS06269: the dashboard is being set up again (the request, and its question when it has one).
      dashboardBusy: false,
      // AutoCount Full: the plan action in flight, "trial" or "buy"; back from its Checkout, waiting
      // for Stripe to confirm (at most CONFIRM_WAIT_MS), and given up on waiting.
      planBusy: "",
      // The activation sheet that opens before Stripe (activation fee doc §3): open; "buy" (AutoCount
      // Full) or "switch" (the line on the subscription to the one with writes, §8 W-1); the line
      // chosen (writes or read-only); what the live subscription would invoice (null until asked, or
      // with no subscription); the request in flight ("preview", "region" or "pay"); and what went
      // wrong inside it.
      sheet: closedSheet(),
      confirmingPurchase: false,
      confirmLate: false,
      // Writes to the book: the request in flight ("set" or "test"), the last test and its error.
      writesBusy: "",
      writesTest: null,
      writesError: "",
      questions: QUESTIONS,
      downloadUrl: CONNECTOR_DOWNLOAD_URL,
    };
  },
  computed: {
    state() {
      return (this.view && this.view.state) || "";
    },
    isReady() {
      return this.state === "ready";
    },
    isFailed() {
      return this.state === "failed";
    },
    /** start is step 1, approve step 2; setup, setting-up and failed are all step 3. */
    stepNumber() {
      if (this.state === "start") return 1;
      if (this.state === "approve") return 2;
      return 3;
    },
    progressPct() {
      return Math.round(((this.stepNumber - 1) / 3) * 100) + "%";
    },
    agent() {
      return (this.view && this.view.agent) || null;
    },
    connection() {
      return (this.view && this.view.connection) || null;
    },
    setup() {
      return (this.view && this.view.setup) || null;
    },
    /** The connector serves the AutoCount connection right now. */
    online() {
      return Boolean(this.view && this.view.online);
    },
    /** This workspace's admin turned its connector off: { by, utc }, or null. */
    turnedOff() {
      return (this.view && this.view.turnedOff) || null;
    },
    /** Inistate turned every connector off: why, or "". Nothing on this page can change it. */
    connectorsOff() {
      return (this.view && this.view.connectorsOff) || "";
    },
    /** A setup would only fail at its first check: there is no connector to reach. */
    cannotReach() {
      return Boolean(this.connectorsOff) || Boolean(this.turnedOff);
    },
    turnedOffLine() {
      const off = this.turnedOff || {};
      const when = timeAgo(off.utc, this.now);
      return (off.by ? "Turned off by " + off.by : "Turned off") + (when ? ", " + when : "")
        + ". Inistate reads nothing from the book until you turn it on.";
    },
    /** The header's word for the home: tone is the badge's colour. */
    headerStatus() {
      if (this.turnedOff) return { text: "Turned off", tone: "warn" };
      if (this.online) return { text: "Connected, read only", tone: "on" };
      if (this.turningOn) return { text: "Turning on…", tone: "off" };
      return { text: "Offline", tone: "off" };
    },
    connectorStatus() {
      if (this.turnedOff) return { text: "Turned off", tone: "warn" };
      return this.online ? { text: "Online", tone: "on" } : { text: "Offline", tone: "off" };
    },
    /** The connector serving the AutoCount connection, which the off switch is for. */
    switchAgentId() {
      const link = this.connection && this.connection.agent;
      return (link && link.agentId) || undefined;
    },
    /** Before approval there is no connection to serve, so a recent call-in counts as online. */
    agentOnline() {
      return this.online || seenWithin(this.lastSeenUtc, this.now, ONLINE_WINDOW_MS);
    },
    lastSeenUtc() {
      if (this.agent && this.agent.lastSeenUtc) return this.agent.lastSeenUtc;
      const link = this.connection && this.connection.agent;
      return (link && link.lastSeenUtc) || null;
    },
    machineName() {
      const agent = this.agent;
      return (agent && (agent.machineName || agent.agentId)) || "the PC";
    },
    fingerprint() {
      return String((this.agent && this.agent.fingerprint) || "").trim();
    },
    /** "7F3A 91C2 0B4E 55D1" as four boxes, read out against the PC's window one at a time. */
    fingerprintGroups() {
      if (!this.fingerprint) return [];
      const parts = this.fingerprint.split(/\s+/);
      return parts.length > 1 ? parts : this.fingerprint.match(/.{1,4}/g);
    },
    hasBook() {
      return Boolean(this.agent && this.agent.book);
    },
    book() {
      const offered = (this.agent && this.agent.book) || {};
      const sql = (this.connection && this.connection.sql) || {};
      return {
        company: offered.company || "",
        database: offered.database || sql.database || "",
        server: offered.server || sql.server || "",
        version: offered.autocountVersion || "",
      };
    },
    bookName() {
      return this.book.company || this.book.database || "the book picked on the PC";
    },
    sqlUser() {
      const sql = this.connection && this.connection.sql;
      return (sql && sql.user) || "";
    },
    readsAs() {
      return this.sqlUser ? "SQL login " + this.sqlUser : "The connector's Windows account, read only";
    },
    workspaceName() {
      return (clientStore.getters && clientStore.getters.workspaceName) || "this workspace";
    },
    code() {
      const pairing = this.view && this.view.pairing;
      return (pairing && pairing.code) || "";
    },
    expiresIn() {
      const pairing = this.view && this.view.pairing;
      return pairing ? countdown(pairing.expiresUtc, this.now) : "";
    },
    codeExpired() {
      return Boolean(this.code) && !this.expiresIn;
    },
    /** The installer as a full address, for an email read somewhere else. */
    downloadHref() {
      try {
        return new URL(CONNECTOR_DOWNLOAD_URL, window.location.origin).href;
      } catch (err) {
        return CONNECTOR_DOWNLOAD_URL;
      }
    },
    mailtoHref() {
      if (!this.code || this.codeExpired) return "";
      const pairing = this.view.pairing;
      const until = clockTime(pairing.expiresUtc);
      const lines = [
        "Hello,",
        "",
        "Please connect our AutoCount server to Inistate, for the workspace " + this.workspaceName + ":",
        "",
        "1. Download the Inistate Connector for Windows and install it on the AutoCount server:",
        "   " + this.downloadHref,
        "2. Open it and type this code: " + this.code,
        "   Or, at an administrator command prompt: InistateConnector.exe join " + this.code,
        "3. When it asks, pick the account book of " + this.workspaceName + ".",
        "",
        "The connector gets read-only access to that book alone; nothing in AutoCount changes.",
        until
          ? "The code works until " + until + ". If it has run out, ask me for a new one."
          : "If the code has run out, ask me for a new one.",
      ];
      return "mailto:?subject=" + encodeURIComponent("Connect AutoCount to Inistate")
        + "&body=" + encodeURIComponent(lines.join("\r\n"));
    },
    templateModules() {
      const list = this.view && this.view.templateModules;
      return Array.isArray(list) ? list : [];
    },
    /** Each module of the AutoCount template, as the server describes it (older servers send none). */
    moduleStates() {
      const list = this.view && this.view.modules;
      return Array.isArray(list) ? list.filter((m) => m && m.key) : [];
    },
    hasModuleList() {
      return this.moduleStates.length > 0;
    },
    /**
     * The modules the lists show. While AutoCount pricing is on the free tier is not offered (activation
     * fee doc A-1, "hide the free modules first"): a free module nobody added is left out. One already
     * added stays, so it can still be hidden, shown or reinstalled; the gate itself is unchanged.
     */
    shownModules() {
      if (!this.plan) return this.moduleStates;
      return this.moduleStates.filter((m) => m.installed || String(m.tier || "").toLowerCase() !== "free");
    },
    /**
     * The modules to make at the first setup; all of them until someone unticks one, but the
     * optional ones (installByDefault false), which start unticked. On Free the paid ones start
     * unticked: the setup would only report them locked.
     */
    setupChoice: {
      get() {
        return this.setupPicked || this.defaultKeys;
      },
      set(keys) {
        this.setupPicked = Array.isArray(keys) ? keys.slice() : null;
      },
    },
    noModuleChosen() {
      return this.hasModuleList && this.setupChoice.length === 0;
    },
    moduleRows() {
      return this.shownModules.map((m) => {
        const installed = Boolean(m.installed);
        const enabled = installed && Boolean(m.enabled);
        const updateAvailable = installed && Boolean(m.updateAvailable);
        const unavailable = !installed && Boolean(m.unavailable);
        // Paid on Free: nothing to tick, but one added before keeps Hide, Show, Reinstall and Update.
        const locked = this.isLocked(m);
        let note;
        if (!installed) note = m.unavailable || (locked ? this.lockedHint : "Not added");
        else if (locked) note = "Locked until you activate";
        else if (!enabled) note = "Hidden: not in the workspace's menus";
        else if (updateAvailable) note = "A newer design is ready";
        else note = "In your workspace";
        let icon = "table";
        if (unavailable) icon = "ban";
        else if (locked) icon = "lock";
        return {
          key: m.key,
          name: (installed && m.moduleName) || m.name || m.key,
          installed,
          enabled,
          updateAvailable,
          unavailable,
          locked,
          icon,
          addable: !installed && !unavailable && !locked,
          note,
          route: enabled && m.moduleId
            ? { name: "list-view", params: { moduleId: commons.encodeids(m.moduleId) } }
            : null,
        };
      });
    },
    addableRows() {
      return this.moduleRows.filter((row) => row.addable);
    },
    /** The ticked modules that can still be added: one that turned locked since it was ticked is not. */
    pickedKeys() {
      return this.picked.filter((key) => this.addableRows.some((row) => row.key === key));
    },
    updatableKeys() {
      return this.moduleRows.filter((row) => row.updateAvailable).map((row) => row.key);
    },
    installedKeys() {
      return this.moduleRows.filter((row) => row.installed).map((row) => row.key);
    },
    /**
     * SS06269: the server counts the template's dashboard widgets for the modules shown here. None,
     * or a server that does not count them yet (it cannot set the dashboard up either): no button.
     */
    canSetUpDashboard() {
      return Number(this.view && this.view.dashboardWidgets) > 0;
    },
    /**
     * Writes to the book, on or off (a book starts read-only), once it is set up. A server that does
     * not answer `writes` yet has no switch: no card.
     */
    writesCard() {
      const writes = this.view && this.view.writes;
      if (!this.isReady || !writes) return null;
      const count = Number(writes.writableModules) || 0;
      const modules = count === 1 ? "1 module here" : count + " modules here";
      return {
        on: Boolean(writes.on),
        sub: writes.on
          ? "On: " + modules + " can create and change documents in AutoCount, each one only as its activity allows "
            + "(an AI asks you first where it must). Turning writes off makes the book read-only again."
          : "Read-only: Inistate reads the book and changes nothing in it. Turning writes on lets " + modules
            + " create and change documents in AutoCount. The PC's owner can still block writes in the Inistate "
            + "Connector window.",
        changed: writes.changedBy && writes.changedUtc
          ? (writes.on ? "Turned on by " : "Turned off by ") + writes.changedBy + ", " + shortDate(writes.changedUtc) + "."
          : "",
      };
    },
    moduleSummary() {
      const count = (n, one, many) => n + " " + (n === 1 ? one : many);
      const added = this.installedKeys.length;
      const hidden = this.moduleRows.filter((row) => row.installed && !row.enabled).length;
      const parts = [count(added, "module", "modules") + " of " + this.moduleRows.length + " added"];
      if (hidden) parts.push(hidden + " hidden");
      if (this.updatableKeys.length) parts.push(count(this.updatableKeys.length, "with a newer design", "with newer designs"));
      return parts.join(" · ");
    },
    addLabel() {
      if (this.runningAction === "add") return "Adding…";
      const n = this.pickedKeys.length;
      return n ? "Add " + n + (n === 1 ? " module" : " modules") : "Add the ticked modules";
    },
    setupModules() {
      const list = this.setup && this.setup.modules;
      return Array.isArray(list) ? list : [];
    },
    /** The setup's checks, then its modules, as one checklist. */
    checklist() {
      if (!this.setup) return [];
      // A run the server gave up on (it outlived its time) still says "running" inside.
      const stopped = this.isFailed;
      const rows = [];
      (Array.isArray(this.setup.steps) ? this.setup.steps : []).forEach((step) => {
        const row = step && stepRow(step, stopped);
        if (row) rows.push(row);
      });
      this.setupModules.forEach((module, index) => {
        if (module) rows.push(moduleRow(module, index, stopped));
      });
      return rows;
    },
    /** What a screen reader hears as the setup moves on. */
    liveStatus() {
      if (!this.setup) return "";
      const running = this.checklist.find((row) => row.mark === "running");
      const made = this.setupModules.filter((m) => m && (m.state === "created" || m.state === "linked" || m.state === "updated")).length;
      // Modules the plan does not cover were never going to be made.
      const total = this.setupModules.filter((m) => m && m.state !== "locked").length;
      const counted = total ? made + " of " + total + " modules ready" : "";
      return [running ? running.text : "", counted].filter(Boolean).join(", ");
    },
    failedStep() {
      const steps = (this.setup && Array.isArray(this.setup.steps)) ? this.setup.steps : [];
      return steps.find((step) => step && step.state === "failed") || null;
    },
    failureMessage() {
      return (this.setup && this.setup.message)
        || "The setup stopped before it finished. Run it again; it carries on where it stopped.";
    },
    /** The connector was not there. Said once: the server's own message may already say it. */
    reachHint() {
      if (!this.failedStep || this.failedStep.key !== "reach") return false;
      return !/inistate connector is running/i.test(this.failureMessage);
    },
    readyModules() {
      return this.setupModules
        .filter((m) => m && (m.state === "created" || m.state === "linked"))
        .map((m, index) => ({
          key: "ready-" + (m.template || index),
          name: m.name || m.template,
          note: m.state === "linked" ? "Made before, linked again" : "Created by the setup",
          route: m.moduleId
            ? { name: "list-view", params: { moduleId: commons.encodeids(m.moduleId) } }
            : null,
        }));
    },
    missingModules() {
      return this.setupModules
        .filter((m) => m && (m.state === "skipped" || m.state === "failed"))
        .map((m, index) => {
          const name = m.name || m.template;
          const failed = m.state === "failed";
          return {
            key: "missing-" + (m.template || index),
            failed,
            title: failed ? name + " could not be created" : name + " was skipped",
            reason: failed ? failureReason(m.reason) : m.reason || "",
          };
        });
    },
    /** The ready-made workflows the server offers once the setup is ready. */
    workflows() {
      const list = this.view && this.view.workflows;
      return Array.isArray(list) ? list : [];
    },
    workflowCards() {
      return this.workflows.filter(Boolean).map((flow) => {
        const reads = Array.isArray(flow.reads) ? flow.reads.filter(Boolean) : [];
        const modules = Array.isArray(flow.modules) ? flow.modules.filter(Boolean) : [];
        const locked = this.isLocked(flow);
        return {
          key: flow.key,
          name: flow.name || flow.key,
          summary: flow.summary || "",
          added: Boolean(flow.added),
          // Restock reads paid modules: on Free its way in is the trial, or buying once that is used.
          locked,
          unlock: locked ? this.unlockAction : "",
          // A module the book could not feed was skipped at setup: the workflow still works, with
          // less for GNey to go on.
          reads: reads.map((read, index) => {
            const missing = read.present === false;
            const name = read.name || read.key;
            return {
              key: read.key || String(index),
              missing,
              text: (missing ? name + " (not in this book)" : name) + (index < reads.length - 1 ? ", " : ""),
            };
          }),
          links: modules
            .filter((m) => m.added && m.moduleId)
            .map((m, index) => ({
              key: m.key || String(index),
              name: m.name,
              route: { name: "list-view", params: { moduleId: commons.encodeids(m.moduleId) } },
            })),
          // What to do once it is added: the server words it, naming the modules as the workspace calls them.
          next: flow.next || "",
          error: this.workflowErrors[flow.key] || "",
        };
      });
    },
    lastContact() {
      return timeAgo(this.lastSeenUtc, this.now);
    },
    connectorLine() {
      const parts = [];
      if (this.lastContact) parts.push("Last contact " + this.lastContact);
      if (this.agent && this.agent.agentVersion) parts.push("version " + this.agent.agentVersion);
      return parts.join(" · ");
    },
    checkedLine() {
      const checked = timeAgo(this.setup && this.setup.finishedUtc, this.now);
      return checked ? "Checked " + checked + ": it cannot change data" : "";
    },
    busy() {
      return this.approving || this.rejecting || this.disconnecting || this.switching || Boolean(this.moduleBusy)
        || this.dashboardBusy;
    },

    // ---- AutoCount Full -------------------------------------------------------------------------

    /**
     * The connector's plan, or null while Inistate has not switched AutoCount pricing on (or the
     * API predates it): the page then shows no strip and no locks, exactly as before.
     */
    plan() {
      const plan = this.view && this.view.plan;
      return plan && typeof plan === "object" && plan.enforced ? plan : null;
    },
    planOnFree() {
      return Boolean(this.plan) && this.plan.tier === "free";
    },
    /** Buying is open here: Stripe is set up, and no dealer bills the workspace. */
    canBuy() {
      return Boolean(this.plan && this.plan.canBuy && !this.plan.dealerBilled);
    },
    /** How something locked is unlocked from here: the trial while it is unused, else buying. */
    unlockAction() {
      if (!this.planOnFree) return "";
      if (this.plan.trialAvailable) return "trial";
      return this.canBuy ? "buy" : "";
    },
    /** The modules the plan covers: what the first setup makes unless someone chooses. */
    allowedKeys() {
      return this.moduleStates.filter((m) => !this.isLocked(m)).map((m) => m.key);
    },
    /** What the first setup starts with ticked: the allowed modules, less the optional ones. */
    defaultKeys() {
      const optional = new Set(this.moduleStates.filter((m) => m.installByDefault === false).map((m) => m.key));
      return this.allowedKeys.filter((key) => !optional.has(key));
    },
    lockedHint() {
      return this.plan && this.plan.trialAvailable ? "Included in the 14-day trial" : "Locked until you activate";
    },
    /** The trial needs the approved book, so the aside offers it from step 3 only. */
    asideTrial() {
      return this.planOnFree && Boolean(this.plan.trialAvailable) && this.stepNumber === 3
        && this.moduleStates.some((m) => this.isLocked(m));
    },
    billingRoute() {
      return { name: "settings", params: { section: "billing" } };
    },
    /**
     * The activation sheet (activation fee doc §3): { title, countries, confirmedLine, lines, due,
     * unsold, primary, canPay }. Amounts come from the server in the workspace's currency; with a
     * live subscription the "when" rows follow what Stripe says adding the line would invoice.
     */
    sheetView() {
      const plan = this.plan;
      if (!plan || !this.sheet.open) return null;
      if (this.sheet.mode === "switch") return this.switchSheetView;
      const preview = this.sheet.preview || {};
      const writes = this.sheet.writes;
      // The line chosen (§8 W-1): RM199 with writes is the whole price, in place of the read-only one.
      const listed = (writes ? plan.writesPrice : plan.price) || {};
      const currency = String(preview.currency || listed.currency || "").toLowerCase();
      const interval = preview.interval || listed.interval || "month";
      const monthlyAmount = preview.price != null ? Number(preview.price) : Number(listed.amount);
      const activationDue = Boolean(plan.activationDue && plan.activation && Number(plan.activation.amount) > 0);
      const activationAmount = activationDue ? Number(plan.activation.amount) : 0;
      const monthly = priceText({ amount: monthlyAmount, currency, interval });
      const each = interval === "year" ? " every year" : " every month";
      const money = (amount) => moneyText(amount, currency);
      const live = Boolean(plan.hasSubscription);
      const keeping = plan.state === "trial";
      const trialEnd = shortDate(plan.trialEndsUtc);

      // Read-only or with writes, each at its own whole price; offered while the server prices the line with writes.
      const choices = plan.writesPrice && priceText(plan.price)
        ? [
          { writes: false, label: "Read only", price: priceText(plan.price), detail: "Every module and Restock. Nothing in AutoCount changes." },
          { writes: true, label: "With writes to AutoCount", price: priceText(plan.writesPrice), detail: "Also create and change documents in your book." },
        ].map((c) => ({ ...c, on: c.writes === writes }))
        : [];

      const lines = [];
      if (activationDue) lines.push({ key: "activation", label: "Activation, one-time", value: money(activationAmount) });
      if (monthly) {
        lines.push({ key: "monthly", label: writes ? "AutoCount Full with writes" : "AutoCount Full", value: monthly.replace("/", " / ") });
      }

      const due = [];
      const first = activationDue ? " (activation + first " + (interval === "year" ? "year" : "month") + ")" : "";
      const total = money(activationAmount + monthlyAmount);
      const onNext = (activationDue ? money(activationAmount) + " + " : "") + monthly;
      if (monthly && live) {
        const starts = shortDate(preview.startsAt);
        if (starts) {
          due.push({ key: "today", label: "Due today", value: money(0) });
          due.push({ key: "then", label: "From " + starts + ", on your subscription", value: onNext, strong: true });
        } else {
          due.push({ key: "added", label: "Added to your subscription", value: "today" });
          due.push({ key: "next", label: "On your next invoice", value: onNext, strong: true });
          if (Number(preview.amountDue) > 0) {
            due.push({ key: "prorated", label: "This period, prorated", value: money(preview.amountDue) });
          }
        }
      } else if (monthly && keeping) {
        due.push({ key: "today", label: "Due today", value: money(0) });
        due.push({ key: "then", label: (trialEnd ? "On " + trialEnd : "When the trial ends") + first, value: total, strong: true });
        due.push({ key: "after", label: "Then", value: money(monthlyAmount) + each });
      } else if (monthly) {
        due.push({ key: "today", label: "Due today" + first, value: total, strong: true });
        due.push({ key: "after", label: "Then", value: money(monthlyAmount) + each });
      }

      const country = BILLING_COUNTRIES.find((c) => c.region === plan.region);
      const confirmed = Boolean(plan.regionConfirmed) || live;
      const sellable = this.canBuy && (!writes || Boolean(plan.canBuyWrites));
      let title = keeping ? "Keep AutoCount Full" : "Activate AutoCount Full";
      if (!keeping && !activationDue && plan.activationPaidUtc) title = "Turn AutoCount Full on again";
      return {
        title,
        intro: "Every module of your book, plus Restock, for GNey, dashboards and your AI assistants.",
        choices,
        countries: confirmed || plan.dealerBilled ? [] : BILLING_COUNTRIES.map((c) => ({
          ...c,
          on: c.region === plan.region,
        })),
        confirmedLine: confirmed && CURRENCY_NAMES[currency]
          ? "Charged in " + CURRENCY_NAMES[currency]
            + (live ? ", the currency of your subscription." : ", set by your first payment.")
          : "",
        lines,
        activation: activationDue,
        due,
        // Not priced for this country (no Stripe price in its currency): nothing to buy from here.
        unsold: !monthly
          ? "AutoCount Full is not sold online " + (country && country.region !== "US" ? "in " + country.label : "for this country")
            + " yet. Talk to us and we will set it up."
          : (sellable ? "" : (writes ? "AutoCount Full with writes" : "AutoCount Full")
            + " cannot be bought online here yet. Talk to us and we will set it up."),
        primary: this.sheet.busy === "pay"
          ? (live ? "Adding…" : "Opening Stripe…")
          : (live ? "Add to subscription" : "Continue to payment"),
        canPay: Boolean(monthly) && sellable && !this.sheet.busy && !(live && !this.sheet.preview),
      };
    },
    /** The sheet moving the line on the subscription to the one with writes (§8 W-1): no activation, no country. */
    switchSheetView() {
      const plan = this.plan;
      const preview = this.sheet.preview || {};
      const listed = plan.writesPrice || {};
      const currency = String(preview.price && preview.price.currency || listed.currency || "").toLowerCase();
      const interval = (preview.price && preview.price.interval) || listed.interval || "month";
      const amount = preview.price && preview.price.amount != null ? Number(preview.price.amount) : Number(listed.amount);
      const withWrites = priceText({ amount, currency, interval });
      const readOnly = priceText(plan.price);
      const due = [];
      if (withWrites && this.sheet.preview) {
        if (Number(preview.amountDue) > 0) {
          due.push({ key: "prorated", label: "This period, prorated, on your next invoice", value: moneyText(preview.amountDue, currency), strong: true });
        }
        due.push({ key: "after", label: "Then", value: moneyText(amount, currency) + (interval === "year" ? " every year" : " every month") });
      }
      return {
        title: "Add writes to AutoCount",
        intro: "Create and change documents in your book from Inistate, GNey and your AI assistants.",
        choices: [],
        countries: [],
        confirmedLine: readOnly ? "In place of " + readOnly + " read-only, not on top of it." : "",
        lines: withWrites ? [{ key: "monthly", label: "AutoCount Full with writes", value: withWrites.replace("/", " / ") }] : [],
        activation: false,
        due,
        unsold: plan.canBuyWrites ? "" : "AutoCount Full with writes cannot be bought online here yet. Talk to us and we will set it up.",
        primary: this.sheet.busy === "pay" ? "Switching…" : "Switch to writes",
        canPay: Boolean(withWrites) && Boolean(plan.canBuyWrites) && !this.sheet.busy && Boolean(this.sheet.preview),
      };
    },
    /** Writes need the line with writes before they can go on (the trial and a deal include them). */
    writesNeedPlan() {
      return Boolean(this.plan) && !this.plan.writes;
    },
    /** The writes card's word on the plan: what writes cost, and the way back to read-only. */
    writesPlan() {
      const plan = this.plan;
      if (!plan || !plan.writesPrice) return null;
      const withWrites = priceText(plan.writesPrice);
      if (plan.writesOnLine && plan.onSubscription) {
        return { note: "Your plan includes writes: " + withWrites + ".", readOnly: priceText(plan.price) };
      }
      if (plan.writes && plan.state === "trial") {
        return { note: "Writes are included in the trial. To keep them after it, choose AutoCount Full with writes ("
          + withWrites + ") when you keep it.", readOnly: "" };
      }
      if (!plan.writes) return { note: "Writes are part of AutoCount Full with writes: " + withWrites + ".", readOnly: "" };
      return null;
    },
    /**
     * The plan strip: { tone, icon, title, detail, action, buyLabel, billing }. `action` is the
     * strip's button ("trial" or "buy"); `billing` names a link to Billing & invoices.
     */
    planStrip() {
      const plan = this.plan;
      if (!plan) return null;
      const price = priceText(plan.price);
      const offer = offerText(plan);
      const activation = activationText(plan);
      const strip = { tone: "free", icon: "lock", title: "", detail: "", action: "", buyLabel: "", billing: "" };
      // Locked, and buying turns it on: with the activation still owed, its price and the monthly's.
      const activate = activation && price ? "Activate: " + activation + " + " + price : "Activate";
      switch (plan.state) {
        case "free":
          strip.title = plan.trialAvailable ? "AutoCount Full: 14 days free" : "AutoCount Full";
          if (plan.dealerBilled) strip.detail = "Ask your vendor for AutoCount Full.";
          else if (plan.trialAvailable) {
            strip.detail = "Every module of your book, plus Restock." + (offer ? " Then " + offer + "." : "");
          } else strip.detail = "Your AutoCount modules are locked until you activate. Nothing is deleted.";
          strip.action = this.unlockAction;
          strip.buyLabel = activate;
          return strip;
        case "trial": {
          strip.tone = "full";
          strip.icon = "check-circle";
          strip.title = this.trialTitle(plan);
          const end = shortDate(plan.trialEndsUtc);
          if (plan.continues) {
            const on = shortDate(plan.startAtUtc || plan.trialEndsUtc);
            strip.title += on ? " · continues on " + on : " · it continues after that";
            // Kept during the trial: the activation lined up waits for the same invoice as the first month.
            const lined = plan.activationLinedUp && plan.activation
              ? moneyText(plan.activation.amount, plan.activation.currency) + " activation + " + price
              : offer;
            if (lined) strip.detail = (on ? "On " + on + ": " : "Then ") + lined + ".";
          } else if (this.canBuy) {
            strip.detail = offer ? "Keep it: " + offer + (end ? " from " + end : "") + "." : "";
            strip.action = "buy";
            strip.buyLabel = "Keep AutoCount Full";
          } else if (plan.dealerBilled) {
            strip.detail = "Ask your vendor to keep AutoCount Full.";
          }
          return strip;
        }
        case "active": {
          strip.tone = "full";
          strip.icon = "check-circle";
          strip.title = "AutoCount Full";
          const paid = shortDate(plan.activationPaidUtc);
          // The line with writes is its own whole price (§8 W-1).
          const line = plan.writesOnLine && priceText(plan.writesPrice) ? priceText(plan.writesPrice) + " with writes" : price;
          strip.detail = [line, paid ? "activation paid " + paid : ""].filter(Boolean).join(" · ");
          strip.billing = "Manage";
          return strip;
        }
        case "grace": {
          const by = shortDate(plan.graceUntil);
          strip.tone = "warn";
          strip.icon = "exclamation-triangle";
          strip.title = "Payment failed: update your card" + (by ? " by " + by : "");
          strip.detail = "AutoCount Full stays on until then.";
          strip.billing = "Update card";
          return strip;
        }
        case "ending": {
          const until = shortDate(plan.paidThroughUtc);
          strip.tone = "full";
          strip.icon = "info-circle";
          strip.title = until ? "AutoCount Full until " + until : "AutoCount Full, cancelled";
          strip.detail = "Cancelled. Nothing is deleted when it ends.";
          return strip;
        }
        case "trial_ended":
          strip.action = this.canBuy ? "buy" : "";
          if (plan.dealerBilled) {
            strip.title = "Trial ended";
            strip.detail = "Ask your vendor for AutoCount Full.";
          } else if (plan.activationPaidUtc || plan.activationWaived) {
            // Bought once and ended since: the activation is paid for this workspace (A-3), or staff waived it.
            strip.title = plan.activationPaidUtc ? "AutoCount Full ended" : "Trial ended";
            strip.detail = "Your AutoCount modules are locked. "
              + (plan.activationPaidUtc ? "Activation is already paid" : "No activation is due")
              + " for this workspace, so only the monthly applies.";
            strip.buyLabel = price ? "Turn on again: " + price : "Turn on again";
          } else {
            strip.title = "Trial ended";
            strip.detail = "Your AutoCount modules are locked until you activate. Nothing is deleted.";
            strip.buyLabel = activate;
          }
          return strip;
        case "manual": {
          const until = shortDate(plan.validUntil);
          strip.tone = "full";
          strip.icon = "info-circle";
          strip.title = "Billed by " + (plan.billedBy || "your vendor");
          strip.detail = until ? "AutoCount Full until " + until : "AutoCount Full";
          return strip;
        }
        case "payment_failed":
          strip.tone = "warn";
          strip.icon = "exclamation-triangle";
          strip.title = "Payment failed: AutoCount Full is off";
          strip.detail = "Your AutoCount modules are locked until it is paid. Nothing is deleted.";
          // A subscription that is still there comes back once the card works; without one, buy again.
          if (plan.hasSubscription) strip.billing = "Update card";
          else if (this.canBuy) {
            strip.action = "buy";
            strip.buyLabel = activation ? activate : (price ? "Turn on again: " + price : "Turn on again");
          }
          return strip;
        default:
          return null;
      }
    },
  },
  methods: {
    message(err, fallback) {
      const data = err && err.response && err.response.data;
      return (data && (data.message || data.error_description)) || fallback;
    },
    statusOf(err) {
      return err && err.response && err.response.status;
    },
    /** The server's words for a refusal; else ours for its code; else the fallback. */
    refusal(err, codes, fallback) {
      const data = err && err.response && err.response.data;
      const code = data && data.error;
      return this.message(err, (code && codes[code]) || fallback);
    },
    /** Paid on Free. Only while AutoCount pricing is on: with it off nothing is locked. */
    isLocked(item) {
      return Boolean(this.plan) && Boolean(item && item.locked);
    },
    /** "Full trial: 9 days left". */
    trialTitle(plan) {
      const days = plan.daysLeft;
      if (days != null && isFinite(days)) {
        if (days <= 0) return "Full trial: ends today";
        return "Full trial: " + days + (days === 1 ? " day left" : " days left");
      }
      const until = shortDate(plan.trialEndsUtc);
      return until ? "Full trial until " + until : "Full trial";
    },
    /** Still the page that asked: not destroyed, and the workspace has not changed since. */
    isCurrent(epoch) {
      return !this.destroyed && epoch === this.epoch;
    },
    stepClass(n) {
      if (n < this.stepNumber) return "is-done";
      return n === this.stepNumber ? "is-current" : "is-later";
    },

    // ---- the page state ---------------------------------------------------------------------

    async load() {
      const epoch = ++this.epoch;
      this.generation += 1;
      this.clearPoll();
      this.loading = true;
      this.unavailable = "";
      this.error = "";
      this.errorFromPoll = false;
      this.view = null;
      this.stopped = false;
      this.approving = false;
      this.rejecting = false;
      this.disconnecting = false;
      this.settingUp = false;
      this.pairingBusy = false;
      this.codeRequested = false;
      this.autoSetupStarted = false;
      this.switching = false;
      this.turningOn = false;
      this.turnOnTimedOut = false;
      this.addingWorkflow = "";
      this.workflowErrors = {};
      this.setupPicked = null;
      this.picked = [];
      this.moduleBusy = "";
      this.runningAction = "";
      this.updatingKeys = [];
      this.planBusy = "";
      try {
        const res = await api.getAutoCount(this.workspaceId);
        if (!this.isCurrent(epoch)) return;
        this.applyView(res && res.data);
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        if (this.statusOf(err) === 404) this.unavailable = UNAVAILABLE;
        else this.error = this.message(err, "AutoCount is not available right now.");
      } finally {
        if (this.isCurrent(epoch)) {
          this.loading = false;
          this.schedule();
        }
      }
    },
    /**
     * Shows an answer, and does what a state asks of the page by itself: a code when there is
     * none (once each time the page lands on step 1), and the setup when the PC is approved and
     * it has not run (once per visit, and not while the connector is off; the step offers a
     * button after that).
     */
    applyView(view) {
      if (!view || typeof view !== "object") return;
      const before = this.state;
      this.view = view;
      this.now = Date.now();
      if (this.errorFromPoll || (before && view.state !== before)) {
        this.error = "";
        this.errorFromPoll = false;
      }

      // Turned back on: done once the connector is back (or someone turned it off again), and
      // given up on after a while, with a way to look again.
      if (this.turningOn && (view.online || view.turnedOff)) {
        this.turningOn = false;
      } else if (this.turningOn && Date.now() >= this.turnOnUntil) {
        this.turningOn = false;
        this.turnOnTimedOut = true;
      }
      if (view.online || view.turnedOff) this.turnOnTimedOut = false;

      // Back from Checkout: done once the plan is Full other than by the trial alone — active, or,
      // bought during the trial, the trial now continuing into the purchase (the server keeps
      // state "trial" with continues:true until it ends) — and given up on after a while.
      if (this.confirmingPurchase) {
        const plan = view.plan;
        if (plan && plan.tier === "full" && (plan.state !== "trial" || plan.continues)) {
          this.confirmingPurchase = false;
          this.$toasted.success("AutoCount Full is on");
        } else if (Date.now() >= this.confirmUntil) {
          this.confirmingPurchase = false;
          this.confirmLate = true;
        }
      }

      if (view.state !== "start") this.codeRequested = false;
      if (view.state === "start" && !view.pairing && !this.codeRequested) {
        this.codeRequested = true;
        this.requestCode(false);
      }
      const reachable = !view.connectorsOff && !view.turnedOff;
      if (view.state === "setup" && reachable && !this.autoSetupStarted && !this.settingUp) {
        this.autoSetupStarted = true;
        this.runSetup();
      }
    },
    /** An action's answer outranks any poll still on its way. */
    applyAction(res) {
      this.generation += 1;
      this.applyView(res && res.data);
      this.schedule();
    },
    /**
     * SS06286: what was just changed here (a module shown or hidden, modules added, the dashboard set
     * up again) is part of the workspace the app already holds: its menus and its dashboard. The app
     * reloads it when the server's push arrives, but only after a wait that spreads everyone's
     * reloads out, so whoever made the change went back to a workspace that did not have it yet.
     * Reloaded here at once, in the background: a reload that fails leaves the workspace as it is.
     */
    refreshWorkspace() {
      try {
        const reload = clientStore.dispatch("loadWorkspace", {
          id: this.workspaceId,
          forceReload: true,
          preserveMenuState: true,
          background: true,
        });
        if (reload && reload.catch) reload.catch((err) => console.error(err));
      } catch (err) {
        console.error(err);
      }
    },

    // ---- polling ----------------------------------------------------------------------------

    pollDelay() {
      if (this.destroyed || this.stopped || this.unavailable || !this.view) return null;
      if (this.settingUp) return SETUP_POLL_MS;
      if (this.turningOn || this.confirmingPurchase) return POLL_MS;
      return POLLED_STATES.indexOf(this.view.state) >= 0 ? POLL_MS : null;
    },
    schedule() {
      this.clearPoll();
      const delay = this.pollDelay();
      if (delay != null) this.pollTimer = setTimeout(() => this.poll(), delay);
    },
    clearPoll() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.pollTimer = null;
    },
    async poll() {
      this.pollTimer = null;
      // One at a time: the poll in flight re-arms the timer when it lands.
      if (this.destroyed || this.polling) return;
      this.polling = true;
      const epoch = this.epoch;
      const generation = this.generation;
      try {
        const res = await api.getAutoCount(this.workspaceId);
        if (!this.isCurrent(epoch) || generation !== this.generation) return;
        this.applyView(res && res.data);
      } catch (err) {
        if (!this.isCurrent(epoch) || generation !== this.generation) return;
        const status = this.statusOf(err);
        if (status === 404) {
          this.unavailable = UNAVAILABLE;
          this.stopped = true;
          return;
        }
        // Asking again will not change a refusal; it may get past a hiccup.
        this.stopped = status === 400 || status === 401 || status === 403;
        this.error = this.message(err, this.stopped
          ? "This page could not be refreshed."
          : "This page could not be refreshed. Trying again…");
        this.errorFromPoll = true;
      } finally {
        this.polling = false;
        if (!this.destroyed) this.schedule();
      }
    },

    // ---- step 1: the code -------------------------------------------------------------------

    newCode() {
      this.requestCode(true);
    },
    async requestCode(renew) {
      if (this.pairingBusy) return;
      const epoch = this.epoch;
      this.pairingBusy = true;
      try {
        const res = await api.createPairing(this.workspaceId, renew);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        if (renew) this.$toasted.success("New code made. The old one no longer works.");
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "A code could not be made. Try again in a moment.");
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.pairingBusy = false;
      }
    },
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.code);
        this.copied = true;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        this.$toasted.global.error({}, "Copying was blocked — select the code and copy it by hand.");
      }
    },

    // ---- step 2: approve or refuse the PC ----------------------------------------------------

    async approve() {
      const agent = this.agent;
      if (!agent || this.busy || this.settingUp) return;
      const epoch = this.epoch;
      let approved = false;
      this.approving = true;
      this.error = "";
      try {
        const res = await api.approveAutoCount(this.workspaceId, agent.agentId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        approved = true;
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "The PC could not be approved.");
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.approving = false;
      }
      // "Approve and set up": the setup follows at once (applyView may have started it already).
      if (approved) this.runSetup();
    },
    async reject() {
      const agent = this.agent;
      if (!agent || this.busy || this.settingUp) return;
      const name = this.machineName;
      const epoch = this.epoch;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Forget " + name + "?",
        text: "Inistate refuses this connector from now on, and it can no longer join with its code. "
          + "To connect the right PC, give it a new code.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          {
            title: "Forget this PC",
            bind: { color: "danger" },
            default: true,
            handler: ({ resolve }) => resolve(true),
          },
        ],
      });
      if (confirmed !== true || !this.isCurrent(epoch)) return;

      this.rejecting = true;
      this.error = "";
      try {
        const res = await api.rejectAutoCount(this.workspaceId, agent.agentId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.$toasted.success("Inistate forgot " + name + ".");
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "That PC could not be refused.");
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.rejecting = false;
      }
    },

    // ---- step 3 and the home ------------------------------------------------------------------

    /**
     * The setup as the step and the home's button run it. Before the first run it makes what was
     * ticked under "What you get"; from the home it checks the book again and links what is there,
     * without adding the modules nobody chose (those are added one by one).
     */
    runSetup() {
      return this.setupRun(this.plainRunOptions(), "");
    },
    plainRunOptions() {
      if (!this.hasModuleList) return null;
      const all = this.moduleStates.map((m) => m.key);
      const keys = this.isReady ? this.installedKeys : this.setupChoice;
      // On Free the first setup's untouched choice is everything the plan covers but the optional
      // modules: sent as nothing chosen, so the server makes what the tier allows (leaving the
      // optional ones out too) and reports the rest as locked.
      if (!this.isReady && this.planOnFree && sameKeys(keys, this.defaultKeys)) return null;
      return keys.length && keys.length < all.length ? { modules: keys } : null;
    },
    /**
     * Answers when the setup has finished (with its answer, or null); meanwhile the page polls
     * faster to show it moving. `options` limits it to some modules, or reinstalls them.
     */
    async setupRun(options, action) {
      if (this.settingUp) return null;
      const epoch = this.epoch;
      this.settingUp = true;
      this.runningAction = action || "";
      this.error = "";
      this.errorFromPoll = false;
      this.schedule();
      try {
        const res = options
          ? await api.runAutoCountSetup(this.workspaceId, options)
          : await api.runAutoCountSetup(this.workspaceId);
        if (!this.isCurrent(epoch)) return null;
        this.settingUp = false;
        this.applyAction(res);
        this.refreshWorkspace();
        return res;
      } catch (err) {
        if (!this.isCurrent(epoch)) return null;
        this.error = this.message(err, "The setup could not run. Try again in a moment.");
        return null;
      } finally {
        if (epoch === this.epoch) {
          this.settingUp = false;
          this.runningAction = "";
          this.updatingKeys = [];
          if (!this.destroyed) this.schedule();
        }
      }
    },
    /**
     * SS06315: a module the book could feed only in part (an older AutoCount without some of its
     * columns) was made or updated without those fields; the server says which, and so does the page.
     */
    reportLeftOut(modules) {
      modules.filter((m) => m.reason).forEach((m) => this.$toasted.info(m.name + ": " + m.reason));
    },
    /** Says which of the asked-for modules did not make it, and why, under the card's heading. */
    reportRefused(res, keys, verb) {
      const refused = runModules(res).filter((m) => keys.indexOf(m.template) >= 0
        && (m.state === "skipped" || m.state === "failed" || m.state === "locked"));
      if (refused.length) {
        this.error = refused
          .map((m) => (m.name || m.template) + " could not be " + verb + ": " + failureReason(m.reason))
          .join(" ");
      }
    },
    /** Makes the ticked modules: a setup run with just those, which checks the book first. */
    async addPicked() {
      const keys = this.pickedKeys.slice();
      if (!keys.length || this.busy || this.settingUp) return;
      const res = await this.setupRun({ modules: keys }, "add");
      if (!res) return;
      this.picked = [];
      const made = runModules(res).filter((m) => m.state === "created" && keys.indexOf(m.template) >= 0);
      if (made.length === 1) this.$toasted.success(made[0].name + " added");
      else if (made.length > 1) this.$toasted.success(made.length + " modules added");
      this.reportLeftOut(made);
      this.reportRefused(res, keys, "added");
    },
    /**
     * Gives modules the AutoCount template's latest design: the "newer design" ones, or one that
     * someone changed in Studio and wants back as it was. Asks first, since Studio changes go.
     */
    async updateModules(keys) {
      if (!keys || !keys.length || this.busy || this.settingUp) return;
      const rows = this.moduleRows.filter((row) => keys.indexOf(row.key) >= 0);
      if (!rows.length) return;
      const one = rows.length === 1 ? rows[0] : null;
      const verb = one && !one.updateAvailable ? "Reinstall" : "Update";
      const epoch = this.epoch;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: one ? verb + " " + one.name + "?" : "Update " + rows.length + " modules?",
        text: (one ? "Its design is" : "Their designs are") + " replaced with the latest from the AutoCount "
          + "template, so changes made to " + (one ? "it" : "them") + " in Studio are lost. "
          + (one ? "Its name, its icon and whether it is shown stay" : "Their names, icons and whether they are shown stay")
          + ", and nothing in AutoCount changes.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: verb, default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true || !this.isCurrent(epoch)) return;

      this.updatingKeys = rows.map((row) => row.key);
      const res = await this.setupRun({ modules: this.updatingKeys.slice(), reinstall: true }, "update");
      if (!res) return;
      const done = runModules(res).filter((m) => m.state === "updated");
      if (done.length === 1) this.$toasted.success(done[0].name + (verb === "Reinstall" ? " reinstalled" : " updated"));
      else if (done.length > 1) this.$toasted.success(done.length + " modules updated");
      this.reportLeftOut(done);
      this.reportRefused(res, keys, "updated");
    },
    /**
     * SS06269: sets the AutoCount starter dashboard up again: the template's shortcuts and charts for
     * the modules shown here. Missing ones are simply added. When a changed widget would go back to
     * the template's, or an outdated one would be removed, the server writes nothing and says what
     * would change (409 confirm_replace): the page asks, and only then sends `replace`. Widgets the
     * workspace made itself are never touched, and the connector is not needed.
     */
    async setUpDashboard() {
      if (this.busy || this.settingUp) return;
      const epoch = this.epoch;
      this.dashboardBusy = true;
      this.error = "";
      this.errorFromPoll = false;
      try {
        let res;
        try {
          res = await api.setUpAutoCountDashboard(this.workspaceId, false);
        } catch (err) {
          const data = err && err.response && err.response.data;
          if (this.statusOf(err) !== 409 || !data || data.error !== "confirm_replace") throw err;
          if (!this.isCurrent(epoch)) return;
          const confirmed = await this.$confirmation.show({
            type: "confirm",
            title: "Replace AutoCount widgets on the dashboard?",
            text: dashboardReplaceText(data.dashboard),
            buttons: [
              { title: "Cancel", bind: { text: true } },
              { title: "Replace", default: true, handler: ({ resolve }) => resolve(true) },
            ],
          });
          if (confirmed !== true || !this.isCurrent(epoch)) return;
          res = await api.setUpAutoCountDashboard(this.workspaceId, true);
        }
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.refreshWorkspace();
        this.$toasted.success(dashboardDoneText(res && res.data && res.data.dashboard));
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "The dashboard could not be set up again. Try again in a moment.");
      } finally {
        if (epoch === this.epoch) this.dashboardBusy = false;
      }
    },
    /**
     * Hides a module (it leaves the menus and the module list connected AI apps get, and keeps its design), or shows it
     * again. Only hiding asks first. Nothing in AutoCount changes, so neither off switch holds it.
     */
    async toggleModule(row) {
      if (!row || !row.installed || this.busy || this.settingUp) return;
      const hide = row.enabled;
      const epoch = this.epoch;
      if (hide) {
        const confirmed = await this.$confirmation.show({
          type: "confirm",
          title: "Hide " + row.name + "?",
          text: "It leaves the workspace's menus and the module list that connected AI apps get. Its design stays, and Show brings "
            + "it back. Nothing in AutoCount changes.",
          buttons: [
            { title: "Cancel", bind: { text: true } },
            { title: "Hide", default: true, handler: ({ resolve }) => resolve(true) },
          ],
        });
        if (confirmed !== true || !this.isCurrent(epoch)) return;
      }

      this.moduleBusy = row.key;
      this.error = "";
      try {
        const res = await api.setAutoCountModuleEnabled(this.workspaceId, row.key, !hide);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.refreshWorkspace();
        this.$toasted.success(row.name + (hide ? " hidden" : " shown"));
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, row.name + " could not be " + (hide ? "hidden." : "shown."));
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.moduleBusy = "";
      }
    },
    async disconnect() {
      if (this.busy || this.settingUp) return;
      const epoch = this.epoch;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Disconnect AutoCount?",
        text: "Inistate stops reading " + this.bookName + " and forgets the connector. Your AutoCount "
          + "modules stay, without new data, and nothing in AutoCount changes.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          {
            title: "Disconnect",
            bind: { color: "danger" },
            default: true,
            handler: ({ resolve }) => resolve(true),
          },
        ],
      });
      if (confirmed !== true || !this.isCurrent(epoch)) return;

      this.disconnecting = true;
      this.error = "";
      try {
        const res = await api.disconnectAutoCount(this.workspaceId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.$toasted.success("AutoCount disconnected");
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "AutoCount could not be disconnected.");
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.disconnecting = false;
      }
    },

    // ---- the off switch -------------------------------------------------------------------------

    /** Stops every read through the connector at once; it stays joined and approved. */
    /**
     * Turns writes to the book on or off, after the admin agrees to what that means. Writes are paid
     * for separately (§8 W-1): without them in the plan, turning them on offers the line with writes.
     */
    async setWrites(on) {
      if (this.writesBusy || this.busy || this.settingUp) return;
      if (on && this.writesNeedPlan) {
        this.offerWrites();
        return;
      }
      const epoch = this.epoch;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: on ? "Turn writes to AutoCount on?" : "Make the book read-only?",
        text: on
          ? "The AutoCount modules that can write will create and change documents in this book, through the "
            + "connector on the PC. Each activity says who may run it; an AI asks you before anything that posts "
            + "or cannot be undone. You can turn writes off again at any time."
          : "The AutoCount modules will only read the book. Nothing already in AutoCount changes.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: on ? "Turn writes on" : "Turn writes off", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true || !this.isCurrent(epoch)) return;
      await this.applyWrites(on);
    },
    /** The switch itself, once agreed (or straight after buying the line with writes for it). */
    async applyWrites(on) {
      const epoch = this.epoch;
      this.writesBusy = "set";
      this.writesError = "";
      this.writesTest = null;
      try {
        const res = await api.setAutoCountWrites(this.workspaceId, on);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.refreshWorkspace();
        const change = (res && res.data && res.data.writesChange) || {};
        const reinstall = (change.modules || []).filter((x) => x.state === "needsReinstall").map((x) => x.name);
        this.$toasted.success(on ? "Writes to AutoCount turned on" : "The book is read-only again");
        if (on && reinstall.length) {
          this.writesError = "Reinstall " + reinstall.join(", ") + " to the latest design so "
            + (reinstall.length === 1 ? "it" : "they") + " can write.";
        }
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        const data = err && err.response && err.response.data;
        // An older page's plan, or a plan that changed meanwhile: the server says writes are not paid for.
        if (on && this.statusOf(err) === 402 && data && data.error === "connector_writes_required") {
          this.writesError = "";
          this.offerWrites();
          return;
        }
        this.writesError = this.message(err, on ? "Writes could not be turned on." : "Writes could not be turned off.");
      } finally {
        if (epoch === this.epoch) this.writesBusy = "";
      }
    },
    /**
     * Writes are not in the plan: the line on the subscription moves to the one with writes, or AutoCount
     * Full is bought with writes chosen. Neither possible here: says who to ask.
     */
    offerWrites() {
      const plan = this.plan;
      if (!plan) return;
      if (plan.onSubscription && plan.canBuyWrites) {
        this.openSheet("switch", true);
        return;
      }
      if (this.canBuy && !plan.onSubscription) {
        this.openSheet("buy", true);
        return;
      }
      this.writesError = plan.dealerBilled
        ? "Writes to AutoCount are arranged through your vendor: ask them to add writes."
        : "Writes to AutoCount cannot be added online here yet. Talk to us and we will set it up.";
    },
    /** Back to the read-only line (§8 W-1): writes stop now, and the book is made read-only first. */
    async switchToReadOnly() {
      const plan = this.plan;
      if (!plan || this.writesBusy || this.busy) return;
      const epoch = this.epoch;
      const readOnly = priceText(plan.price);
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Switch to read-only?",
        text: "Writes to AutoCount stop now and the book becomes read-only. "
          + (readOnly ? "AutoCount Full is " + readOnly + " from your next invoice. " : "")
          + "Nothing already in AutoCount changes, and nothing is refunded.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "Switch to read-only", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true || !this.isCurrent(epoch)) return;
      this.writesBusy = "plan";
      this.writesError = "";
      try {
        if (this.writesCard && this.writesCard.on) await api.setAutoCountWrites(this.workspaceId, false);
        if (!this.isCurrent(epoch)) return;
        await api.changeAutoCountWrites(this.workspaceId, false);
        if (!this.isCurrent(epoch)) return;
        this.$toasted.success("AutoCount Full is read-only now");
        await this.reloadView(epoch);
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.writesError = this.refusal(err, BUY_REFUSALS, "The plan could not be changed. Try again in a moment.");
      } finally {
        if (epoch === this.epoch) this.writesBusy = "";
      }
    },
    /** Tests writes without saving anything, and shows each switch a write passes. */
    async testWrites() {
      if (this.writesBusy || this.busy) return;
      const epoch = this.epoch;
      this.writesBusy = "test";
      this.writesError = "";
      try {
        const res = await api.testAutoCountWrites(this.workspaceId);
        if (!this.isCurrent(epoch)) return;
        this.writesTest = writesTestView((res && res.data) || {});
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.writesTest = null;
        this.writesError = this.message(err, "Writes could not be tested.");
      } finally {
        if (epoch === this.epoch) this.writesBusy = "";
      }
    },
    async turnOff() {
      if (this.busy || this.settingUp) return;
      const name = this.machineName;
      const epoch = this.epoch;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Turn AutoCount off?",
        text: "Inistate stops reading AutoCount through " + name + " until you turn it back on. Nothing in "
          + "AutoCount changes and nothing is deleted; the modules show AutoCount as off meanwhile.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          { title: "Turn off", default: true, handler: ({ resolve }) => resolve(true) },
        ],
      });
      if (confirmed !== true || !this.isCurrent(epoch)) return;

      this.switching = true;
      this.error = "";
      this.turningOn = false;
      this.turnOnTimedOut = false;
      try {
        const res = await api.turnOffAutoCount(this.workspaceId, this.switchAgentId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.$toasted.success("AutoCount turned off");
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "AutoCount could not be turned off.");
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.switching = false;
      }
    },
    /**
     * The connector learns it is on again when it next asks, within a minute; until it is back
     * the page asks every few seconds, for a while.
     */
    async turnOn() {
      if (this.switching) return;
      const epoch = this.epoch;
      this.switching = true;
      this.error = "";
      this.turnOnTimedOut = false;
      try {
        const res = await api.turnOnAutoCount(this.workspaceId, this.switchAgentId);
        if (!this.isCurrent(epoch)) return;
        const view = (res && res.data) || {};
        // Before the answer is shown, so the polling it schedules is already the faster kind.
        this.turningOn = !view.online && !view.turnedOff;
        this.turnOnUntil = Date.now() + TURN_ON_WAIT_MS;
        this.applyAction(res);
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "AutoCount could not be turned on.");
        this.errorFromPoll = false;
      } finally {
        if (epoch === this.epoch) this.switching = false;
      }
    },
    /** After the wait ran out: look for the connector again, for as long again. */
    checkAgain() {
      this.turnOnTimedOut = false;
      this.turningOn = true;
      this.turnOnUntil = Date.now() + TURN_ON_WAIT_MS;
      this.poll();
    },

    // ---- ready-made workflows ---------------------------------------------------------------------

    setWorkflowError(key, message) {
      const errors = { ...this.workflowErrors };
      if (message) errors[key] = message;
      else delete errors[key];
      this.workflowErrors = errors;
    },
    /**
     * Creates the workflow's modules, or links those made before. Nothing in AutoCount changes, so
     * neither off switch holds it back. Says per module what happened: a success as a toast, a
     * module that could not be made on the card itself.
     */
    async addWorkflow(flow) {
      if (this.addingWorkflow || this.busy) return;
      const epoch = this.epoch;
      this.addingWorkflow = flow.key;
      this.setWorkflowError(flow.key, "");
      try {
        const res = await api.addAutoCountWorkflow(this.workspaceId, flow.key);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.refreshWorkspace();
        const outcome = (res && res.data && res.data.workflow) || {};
        const modules = Array.isArray(outcome.modules) ? outcome.modules.filter(Boolean) : [];
        modules.forEach((m) => {
          if (m.state === "created") this.$toasted.success(m.name + " added");
          else if (m.state === "linked") this.$toasted.success(m.name + " was already there");
        });
        const failed = modules.filter((m) => m.state === "failed").map((m) => {
          const reason = failureReason(m.reason) || "no reason was given";
          return m.name + " could not be created: " + reason + (/[.!?]$/.test(reason) ? "" : ".");
        });
        if (failed.length) this.setWorkflowError(flow.key, failed.join(" "));
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.setWorkflowError(flow.key, this.message(err, flow.name + " could not be added. Try again in a moment."));
      } finally {
        if (epoch === this.epoch) this.addingWorkflow = "";
      }
    },

    // ---- AutoCount Full ---------------------------------------------------------------------------

    /**
     * Starts the 14-day trial: once per workspace, no card. The answer is the page with Full on, so
     * the paid modules can be added straight away. A refusal (used, no book yet) says why above.
     */
    async startTrial() {
      if (this.planBusy) return;
      const epoch = this.epoch;
      this.planBusy = "trial";
      this.error = "";
      this.errorFromPoll = false;
      try {
        const res = await api.startAutoCountTrial(this.workspaceId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
        this.$toasted.success("Your 14-day AutoCount Full trial has started");
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.refusal(err, TRIAL_REFUSALS, "The trial could not be started. Try again in a moment.");
      } finally {
        if (epoch === this.epoch) this.planBusy = "";
      }
    },
    /**
     * Keep or activate AutoCount Full (§6.3; activation fee doc §3). The sheet opens first: the
     * business country while it is not fixed, the one-time activation and the monthly, and when the
     * money moves. With a live subscription it asks Stripe what adding the line would invoice.
     */
    buyFull() {
      this.openSheet("buy", false);
    },
    /**
     * Opens the sheet: "buy" with the read-only line or the one with writes chosen, or "switch" to move
     * the line already on the subscription to the one with writes (§8 W-1).
     */
    openSheet(mode, writes) {
      const plan = this.plan;
      if (!plan || this.planBusy || this.sheet.open) return;
      this.error = "";
      this.errorFromPoll = false;
      this.lastFocus = typeof document !== "undefined" ? document.activeElement : null;
      this.sheet = { ...closedSheet(), open: true, mode, writes: Boolean(writes) };
      this.$nextTick(() => this.focusSheet());
      if (mode === "switch" || plan.hasSubscription) this.loadSheetPreview();
    },
    /** The line chosen in the sheet: read-only or with writes. A live subscription is asked again. */
    chooseLine(writes) {
      if (!this.sheet.open || this.sheet.busy || this.sheet.writes === writes) return;
      this.sheet.writes = writes;
      this.sheet.preview = null;
      this.sheet.error = "";
      if (this.plan && this.plan.hasSubscription) this.loadSheetPreview();
    },
    focusSheet() {
      const root = this.$refs.sheet;
      if (!root) return;
      const target = root.querySelector(".fts-autocount-sheet-pay") || root.querySelector(FOCUSABLE);
      if (target && typeof target.focus === "function") target.focus();
    },
    closeSheet() {
      if (!this.sheet.open || this.sheet.busy === "pay") return;
      this.sheet = closedSheet();
      const back = this.lastFocus;
      this.lastFocus = null;
      this.$nextTick(() => {
        if (back && document.body.contains(back) && typeof back.focus === "function") back.focus();
      });
    },
    /** Tab stays inside the sheet while it is open. */
    trapSheetFocus(event) {
      const root = this.$refs.sheet;
      if (!root) return;
      const items = Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    /** What adding the line to the live subscription would invoice, for the sheet's "when" rows. */
    async loadSheetPreview() {
      const epoch = this.epoch;
      const writes = this.sheet.writes;
      this.sheet.busy = "preview";
      try {
        const res = this.sheet.mode === "switch"
          ? await api.previewAutoCountWrites(this.workspaceId, true)
          : await api.previewAutoCountFull(this.workspaceId, writes);
        // Dropped when the admin chose the other line meanwhile: its own answer is on its way.
        if (!this.isCurrent(epoch) || !this.sheet.open || this.sheet.writes !== writes) return;
        this.sheet.preview = (res && res.data) || {};
      } catch (err) {
        if (!this.isCurrent(epoch) || !this.sheet.open) return;
        this.sheet.error = this.refusal(err, BUY_REFUSALS, "Stripe's price could not be read. Try again in a moment.");
      } finally {
        if (epoch === this.epoch && this.sheet.busy === "preview") this.sheet.busy = "";
      }
    },
    /** The business country (A-5a): the page comes back priced in its currency. */
    async pickCountry(region) {
      const plan = this.plan;
      if (!plan || this.sheet.busy || region === plan.region) return;
      const epoch = this.epoch;
      this.sheet.busy = "region";
      this.sheet.error = "";
      try {
        await api.setBillingRegion(this.workspaceId, region);
        if (!this.isCurrent(epoch)) return;
        const res = await api.getAutoCount(this.workspaceId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
      } catch (err) {
        if (!this.isCurrent(epoch) || !this.sheet.open) return;
        this.sheet.error = this.refusal(err, REGION_REFUSALS, "The country could not be saved. Try again in a moment.");
      } finally {
        if (epoch === this.epoch && this.sheet.busy === "region") this.sheet.busy = "";
      }
    },
    /**
     * The sheet's button. With no live subscription, Stripe Checkout, which comes back to the app when
     * it is done. With one, the line goes on it (at the trial's end when kept during one). "switch"
     * moves the line already there to the one with writes, then turns writes to the book on.
     */
    async confirmSheet() {
      const plan = this.plan;
      if (!plan || !this.sheetView || !this.sheetView.canPay) return;
      const epoch = this.epoch;
      const writes = this.sheet.writes;
      let leaving = false;
      this.sheet.busy = "pay";
      this.sheet.error = "";
      try {
        if (this.sheet.mode === "switch") {
          await api.changeAutoCountWrites(this.workspaceId, true);
          if (!this.isCurrent(epoch)) return;
          this.sheet.busy = "";
          this.closeSheet();
          this.$toasted.success("Writes to AutoCount added to your plan");
          await this.reloadView(epoch);
          if (this.isCurrent(epoch) && this.writesCard && !this.writesCard.on) await this.applyWrites(true);
          return;
        }
        if (!plan.hasSubscription) {
          const url = await this.checkoutUrl();
          if (!this.isCurrent(epoch)) return;
          if (url) {
            leaving = true;
            window.location.href = url;
            return;
          }
          if (url === "") {
            this.sheet.error = "Stripe did not return a checkout link.";
            return;
          }
          // null: a subscription appeared since the page loaded, so the line goes on it instead. The
          // sheet asks again with that subscription's invoice, and the admin confirms that one.
          this.sheet.busy = "";
          await this.reloadView(epoch);
          if (this.isCurrent(epoch) && this.sheet.open) await this.loadSheetPreview();
          return;
        }
        const res = await api.addAutoCountFull(this.workspaceId, writes);
        if (!this.isCurrent(epoch)) return;
        const added = (res && res.data) || {};
        this.sheet.busy = "";
        this.closeSheet();
        this.$toasted.success(added.status === "scheduled"
          ? "AutoCount Full continues when the trial ends"
          : "AutoCount Full added");
        await this.reloadView(epoch);
        // Bought with writes after asking to turn them on: they go on now (kept during the trial they already work).
        if (writes && this.isCurrent(epoch) && this.writesCard && !this.writesCard.on && this.plan && this.plan.writes) {
          await this.applyWrites(true);
        }
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.sheet.error = this.refusal(err, BUY_REFUSALS, "AutoCount Full could not be bought. Try again in a moment.");
      } finally {
        if (epoch === this.epoch && !leaving && this.sheet.busy === "pay") this.sheet.busy = "";
      }
    },
    /** Checkout's address; "" when Stripe sent none; null when the server says to buy in-app. */
    async checkoutUrl() {
      try {
        const res = await api.checkoutAutoCountFull(this.workspaceId, this.sheet.writes);
        return (res && res.data && res.data.url) || "";
      } catch (err) {
        const data = err && err.response && err.response.data;
        if (this.statusOf(err) === 409 && data && data.error === "use_in_app") return null;
        throw err;
      }
    },
    /**
     * Back from AutoCount Full's Stripe Checkout (`?billing=success|cancel&connector=autocount`; the
     * shell opened this page for it). The redirect only says the customer paid: the plan turns Full
     * when Stripe's webhook lands, so the page asks every few seconds until then, for a while. A
     * return stamped for another workspace is left for that workspace.
     */
    takeCheckoutReturn() {
      let ret = null;
      try {
        ret = readBillingReturn(window.location.search);
      } catch (err) {
        ret = null;
      }
      if (!ret || ret.connector !== "autocount") return;
      if (ret.workspaceId && Number(ret.workspaceId) !== Number(this.workspaceId)) return;
      clearBillingReturn();
      if (ret.kind !== "success") return;
      this.confirmingPurchase = true;
      this.confirmLate = false;
      this.confirmUntil = Date.now() + CONFIRM_WAIT_MS;
    },
    /** After a change made through billing, which does not answer with the page, ask for it again. */
    async reloadView(epoch) {
      try {
        const res = await api.getAutoCount(this.workspaceId);
        if (!this.isCurrent(epoch)) return;
        this.applyAction(res);
      } catch (err) {
        if (!this.isCurrent(epoch)) return;
        this.error = this.message(err, "This page could not be refreshed. Reload it to see AutoCount Full.");
      }
    },
  },
  watch: {
    workspaceId() {
      this.confirmingPurchase = false;
      this.confirmLate = false;
      this.sheet = closedSheet();
      this.load();
    },
  },
  created() {
    // Not reactive: timers and the counters that tell a stale answer from a current one.
    this.epoch = 0;
    this.generation = 0;
    this.pollTimer = null;
    this.polling = false;
    this.stopped = false;
    this.destroyed = false;
    this.copiedTimer = null;
    this.turnOnUntil = 0;
    this.confirmUntil = 0;
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
    this.takeCheckoutReturn();
    this.load();
  },
  beforeDestroy() {
    this.destroyed = true;
    this.clearPoll();
    clearInterval(this.ticker);
    clearTimeout(this.copiedTimer);
  },
};
</script>

<style lang="scss" scoped>
$mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;

.fts-autocount-h1 {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.fts-autocount-hint {
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-mute);
}

.fts-autocount-mono {
  font-family: $mono;
}

.fts-autocount-p {
  margin: 0;
}

/* Visible to screen readers only. */
.fts-autocount-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Online / Offline, Connected. */
.fts-autocount-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 1px 10px;
  border-radius: 999px;
  background: var(--fts-settings-hair);
  color: var(--fts-settings-mute);
  font-size: 0.75rem;
  line-height: 1.25rem;
  font-weight: 600;
  letter-spacing: 0;
  white-space: nowrap;

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--inistate-color-neutral-400);
  }

  &.is-on {
    background: var(--inistate-color-green-50);
    color: var(--inistate-color-green-800);

    i {
      background: var(--inistate-color-green-800);
    }
  }

  &.is-warn {
    background: var(--inistate-color-orange-50);
    color: var(--inistate-color-orange-900);

    i {
      background: var(--inistate-color-orange-900);
    }
  }
}

/* AutoCount Full: the plan the connector is on, its one action on the right. */
.fts-autocount-plan {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  padding: 12px 16px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;

  &.is-full {
    border-color: var(--fts-settings-accent-line);
    background: var(--fts-settings-accent-soft);
  }

  &.is-warn {
    border-color: var(--inistate-color-orange-100);
    background: var(--inistate-color-orange-50);
  }
}

.fts-autocount-plan-icon {
  flex-shrink: 0;
  color: var(--fts-settings-faint);

  .is-full > & {
    color: var(--fts-settings-accent);
  }

  .is-warn > & {
    color: var(--inistate-color-orange-900);
  }
}

.fts-autocount-plan-text {
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-weight: 600;
  }

  span {
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-plan-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* The activation sheet: a dialog over the page, with the business country, the two prices and when they are charged. */
.fts-autocount-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(33, 33, 33, 0.45);
}

.fts-autocount-sheet {
  box-sizing: border-box;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 24px;
  border-radius: 12px;
  background: var(--inistate-color-shade-white);
  color: var(--fts-settings-ink);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.fts-autocount-sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  > div {
    flex: 1;
    min-width: 0;
  }

  h2 {
    margin: 0;
    font-size: 1.125rem;
    line-height: 1.625rem;
    font-weight: 600;
  }

  p {
    margin: 2px 0 0;
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-sheet-close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--fts-settings-mute);
  cursor: pointer;

  &:hover {
    background: var(--fts-settings-ground);
  }
}

/* Read-only or with writes: two real radios drawn as cards, each with its whole price. */
.fts-autocount-sheet-lines-choice {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  legend {
    margin: 0 0 8px;
    padding: 0;
    font-size: 0.8125rem;
    font-weight: 600;
  }
}

.fts-autocount-choice {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: var(--fts-settings-ground);
  }

  &.is-on {
    border-color: var(--fts-settings-accent);
    box-shadow: inset 0 0 0 1px var(--fts-settings-accent);
    background: var(--fts-settings-accent-soft);
  }

  &:focus-within {
    outline: 3px solid var(--fts-settings-accent);
    outline-offset: 2px;
  }

  input {
    flex-shrink: 0;
    margin: 0;
    accent-color: var(--fts-settings-accent);
  }
}

.fts-autocount-choice-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 0.875rem;
    font-weight: 600;
  }

  span {
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-choice-price {
  flex-shrink: 0;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
}

/* A text button inside a line of words: the way back to read-only. */
.fts-autocount-link {
  margin: 0 0 0 4px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--fts-settings-accent);
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    color: var(--fts-settings-mute);
    cursor: default;
  }
}

.fts-autocount-sheet-country {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;

  legend {
    margin: 0 0 8px;
    padding: 0;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .fts-autocount-hint {
    margin: 6px 0 0;
  }
}

/* Three countries as one segmented choice; each is a real radio. */
.fts-autocount-seg {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3px;
  padding: 3px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 9px;
}

.fts-autocount-seg-option {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  margin: 0;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: var(--fts-settings-mute);
  cursor: pointer;

  &:hover {
    color: var(--fts-settings-ink);
    background: var(--fts-settings-ground);
  }

  &.is-on,
  &.is-on:hover {
    background: var(--fts-settings-accent);
    color: var(--fts-settings-on-accent);
  }

  &:focus-within {
    outline: 3px solid var(--fts-settings-accent);
    outline-offset: 2px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    opacity: 0;
    pointer-events: none;
  }
}

.fts-autocount-sheet-lines {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th,
  td {
    padding: 10px 0;
    border-bottom: 1px solid var(--fts-settings-hair);
  }

  th {
    text-align: left;
    font-weight: 400;
  }

  td {
    text-align: right;
    font-weight: 600;
    white-space: nowrap;
  }
}

.fts-autocount-sheet-due {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--fts-settings-ground);
}

.fts-autocount-sheet-due-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2px 16px;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-mute);

  &.is-strong {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--fts-settings-ink);
  }
}

.fts-autocount-sheet-foot {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--fts-settings-hair);
}

@media (max-width: 480px) {
  .fts-autocount-sheet {
    padding: 16px;
  }

  .fts-autocount-sheet-foot .fts-settings-btn {
    flex: 1 1 auto;
  }
}

/* A banner with its one action on the right, wrapping under the words on a phone. */
.fts-autocount-turned-off,
.fts-autocount-turn-on-late {
  flex-wrap: wrap;
  align-items: center;
}

.fts-autocount-banner-text {
  flex: 1 1 260px;
  min-width: 0;
}

/* ---- the steps beside what you get ---- */

.fts-autocount-layout {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px;
}

.fts-autocount-main {
  flex: 999 1 520px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fts-autocount-aside {
  flex: 1 1 280px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fts-autocount-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fts-autocount-progress-text {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 12px;

  strong {
    font-weight: 600;
  }

  span {
    font-size: 0.8125rem;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-progress-track {
  height: 8px;
  border-radius: 999px;
  background: var(--fts-settings-hair);
  overflow: hidden;
}

.fts-autocount-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: var(--fts-settings-accent);
  transition: width 0.3s ease;
}

.fts-autocount-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fts-autocount-step {
  display: flex;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;
  background: var(--inistate-color-shade-white);

  &.is-current {
    border-color: var(--fts-settings-accent);
    box-shadow: inset 0 0 0 1px var(--fts-settings-accent);
  }

  &.is-later {
    background: var(--fts-settings-ground);
  }
}

.fts-autocount-step-mark {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1px solid var(--inistate-color-neutral-300);
  color: var(--fts-settings-mute);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;

  .is-current > & {
    background: var(--fts-settings-accent);
    border-color: var(--fts-settings-accent);
    color: var(--fts-settings-on-accent);
  }

  .is-done > & {
    background: var(--inistate-color-green-50);
    border-color: transparent;
    color: var(--inistate-color-green-800);
  }
}

.fts-autocount-step-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fts-autocount-step-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 32px;
  justify-content: center;

  > strong {
    font-size: 0.9375rem;
    line-height: 1.375rem;
    font-weight: 600;
  }

  > span {
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-step.is-later .fts-autocount-step-head > strong {
  color: var(--fts-settings-ink-2);
}

.fts-autocount-download {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

/* The frame colours every link; a link drawn as a button keeps the button's ink. */
.fts-autocount a.fts-settings-btn {
  color: var(--fts-settings-ink);
  text-decoration: none;
}

.fts-autocount-code {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px 12px 20px;
  border: 1px solid var(--fts-settings-accent-line);
  border-radius: 10px;
  background: var(--fts-settings-accent-soft);

  code {
    font-family: $mono;
    font-size: 1.875rem;
    line-height: 2.5rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--inistate-color-primary-800);
    overflow-wrap: anywhere;
  }

  &.is-expired code {
    color: var(--fts-settings-faint);
    text-decoration: line-through;
  }

  &.is-empty {
    padding: 12px 12px 12px 16px;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-code-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.fts-autocount-new-code {
  font-size: 0.8125rem;
}

/* SS06324: the connector card's link to the latest installer, an ordinary link in the hint size. */
.fts-autocount-update {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
}

.fts-autocount-wait {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
}

.fts-autocount-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  color: var(--fts-settings-ink-2);
}

/* Step 2: what to compare with the PC. */
.fts-autocount-check {
  margin: 0;
  display: grid;
  grid-template-columns: minmax(110px, max-content) minmax(0, 1fr);
  align-items: center;
  gap: 14px 20px;
  padding: 16px 20px;
  border: 1px solid var(--fts-settings-accent-line);
  border-radius: 10px;
  background: var(--fts-settings-accent-soft);

  dt {
    font-weight: 600;
    color: var(--fts-settings-ink-2);
  }

  dd {
    margin: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;

    strong {
      font-weight: 600;
    }
  }
}

.fts-autocount-fingerprint {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  span {
    padding: 6px 12px;
    border: 1px solid var(--fts-settings-line);
    border-radius: 8px;
    background: var(--inistate-color-shade-white);
    font-family: $mono;
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
  }
}

.fts-autocount-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.fts-autocount-retry-row {
  display: flex;
}

/* Step 3: the setup as it happens. */
.fts-autocount-checklist {
  list-style: none;
  margin: 0;
  padding: 6px 16px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 10px;
  display: flex;
  flex-direction: column;

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
  }

  li.is-waiting,
  li.is-skipped,
  li.is-locked {
    color: var(--fts-settings-mute);
  }

  li.is-running .fts-autocount-row-label {
    font-weight: 600;
  }

  li.is-failed .fts-autocount-row-detail {
    color: var(--inistate-color-red-600);
  }
}

.fts-autocount-row-mark {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.0625rem;

  .is-done > & {
    color: var(--inistate-color-green-800);
  }

  .is-failed > & {
    color: var(--inistate-color-red-600);
  }

  .is-skipped > & {
    color: var(--fts-settings-faint);
  }

  .is-locked > & {
    color: var(--inistate-color-primary-800);
  }
}

.fts-autocount-hollow {
  display: block;
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid var(--inistate-color-neutral-300);
}

.fts-autocount-row-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.fts-autocount-row-detail {
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-mute);
  overflow-wrap: anywhere;
}

/* ---- the aside ---- */

.fts-autocount-get {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;

  p {
    margin: 0;
    color: var(--fts-settings-mute);
  }

  ul {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: var(--fts-settings-ink-2);
  }
}

.fts-autocount-get h2,
.fts-autocount-ask h2,
.fts-autocount-other h2 {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.375rem;
  font-weight: 600;
}

.fts-autocount-ask {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border: 1px solid var(--fts-settings-accent-line);
  border-radius: 12px;
  background: var(--fts-settings-accent-soft);

  h2 {
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: var(--fts-settings-accent);
    }
  }

  p {
    margin: 0;
    color: var(--fts-settings-ink-2);
  }
}

.fts-autocount-safe {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 4px;

  span {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    color: var(--fts-settings-ink-2);
  }

  svg {
    width: 16px;
    flex-shrink: 0;
    margin-top: 3px;
    color: var(--fts-settings-accent);
  }
}

/* ---- the home ---- */

.fts-autocount-fact {
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;

  strong {
    font-weight: 600;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    overflow-wrap: anywhere;
  }
}

.fts-autocount-modules {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

@media (max-width: 560px) {
  .fts-autocount-modules {
    grid-template-columns: minmax(0, 1fr);
  }

  .fts-autocount-check {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;

    dd {
      margin-bottom: 10px;
    }
  }
}

.fts-autocount-module {
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 14px 16px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 10px;
  color: var(--fts-settings-ink);
  text-decoration: none;
}

.fts-autocount a.fts-autocount-module {
  color: var(--fts-settings-ink);

  &:hover {
    border-color: var(--fts-settings-accent);
    box-shadow: inset 0 0 0 1px var(--fts-settings-accent);
  }
}

.fts-autocount-module-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--fts-settings-accent-soft);
  color: var(--inistate-color-primary-800);
  display: flex;
  align-items: center;
  justify-content: center;

  /* Locked until the trial or activation. */
  &.is-locked {
    background: var(--fts-settings-hair);
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-module-text {
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  span {
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-missing {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    border: 1px dashed var(--inistate-color-neutral-300);
    border-radius: 10px;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-missing-icon {
  margin-top: 3px;
  color: var(--fts-settings-faint);
}

/* The home's module list: every template module, its state in a word, its actions on the right. */
.fts-autocount-manage-sum {
  margin: 0;
}

.fts-autocount-manage {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fts-autocount-manage-row {
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 10px;

  .fts-autocount-module-text {
    flex: 1 1 220px;
  }

  &.is-absent {
    border-style: dashed;
    border-color: var(--inistate-color-neutral-300);
  }

  &.is-off .fts-autocount-module-text strong {
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-manage-name {
  color: var(--fts-settings-ink);
  text-decoration: none;

  &:hover strong {
    text-decoration: underline;
  }
}

.fts-autocount-manage-pick {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    margin: 0;
    cursor: pointer;
    accent-color: var(--fts-settings-accent);
  }
}

.fts-autocount-manage-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.fts-autocount-manage-foot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.fts-autocount-get .fts-autocount-get-pick {
  list-style: none;
  padding-left: 0;
  gap: 6px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  input {
    margin: 0;
    accent-color: var(--fts-settings-accent);
  }
}

/* "Included in the 14-day trial", under a paid module's name. */
.fts-autocount-get-full {
  display: block;
  padding-left: 21px;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--fts-settings-mute);
}

/* Ready-made workflows: one card each, its action on the right of its name. */
.fts-autocount-workflow {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 10px;
}

.fts-autocount-workflow-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
}

.fts-autocount-workflow-text {
  flex: 1 1 320px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-weight: 600;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  > span {
    font-size: 0.8125rem;
    line-height: 1.25rem;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-workflow-lock {
  color: var(--fts-settings-mute);
}

.fts-autocount-workflow-reads {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--fts-settings-ink-2);
}

.fts-autocount-workflow-read.is-missing {
  color: var(--fts-settings-faint);
}

.fts-autocount-workflow-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.fts-autocount-workflow-link {
  font-size: 0.8125rem;
  font-weight: 500;
}

.fts-autocount-workflow-error {
  margin: 0;
}

.fts-autocount-other {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  border: 1px solid var(--fts-settings-line);
  border-radius: 12px;

  p {
    margin: 2px 0 0;
    color: var(--fts-settings-mute);
  }
}

.fts-autocount-other-icon {
  margin-top: 3px;
  flex-shrink: 0;
  font-size: 1.125rem;
  color: var(--fts-settings-accent);
}

.fts-autocount-danger {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--fts-settings-line);
}
</style>
